import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Building2,
  Users,
  Clock,
  LogOut,
  Menu,
  X,
  Plus,
  ChevronDown,
  Home,
  Settings,
  MoreVertical,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Search,
  TrendingUp,
  FileText,
  BarChart3,
  Pencil,
  DollarSign,
  Receipt,
  CreditCard,
  AlertCircle,
  Download,
  Printer,
  FileDown,
  ExternalLink,
  RefreshCw,
  Calendar,
  CheckSquare,
  MessageCircle,
  Phone,
  Archive,
  RotateCcw,
  Send,
  Handshake,
  ArrowLeft,
  Key,
  Eye as EyeOpen,
  EyeOff
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Super Admin Overview
function SuperAdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estados para modal de funcionários
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };
  
  // Buscar todos os funcionários
  const fetchAllEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(`${API}/superadmin/all-employees`);
      setAllEmployees(response.data);
    } catch (error) {
      toast.error("Erro ao carregar funcionários");
      setAllEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };
  
  // Abrir modal de funcionários
  const openEmployeesModal = () => {
    setShowEmployeesModal(true);
    setEmployeeSearchTerm("");
    fetchAllEmployees();
  };
  
  // Abrir detalhes do funcionário
  const openEmployeeDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
  };
  
  // Filtrar funcionários por busca
  const filteredEmployees = allEmployees.filter(emp => 
    emp.name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    emp.cpf?.includes(employeeSearchTerm) ||
    emp.company_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superadmin-overview">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Painel do Super Admin</h1>
        <p className="text-slate-600">Gerencie todas as empresas do sistema CiaPonto</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Empresas</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{stats?.total_companies || 0}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-ciaponto-cyan" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Empresas Ativas</p>
                <p className="font-heading font-bold text-3xl text-green-600">{stats?.active_companies || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-lg hover:border-[#1CA1BA]/50 transition-all"
          onClick={openEmployeesModal}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Funcionários</p>
                <p className="font-heading font-bold text-3xl text-[#1CA1BA]">{stats?.total_employees || 0}</p>
                <p className="text-xs text-[#17889E] mt-1">Clique para ver todos</p>
              </div>
              <div className="w-12 h-12 bg-[#1CA1BA]/15 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-[#1CA1BA]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Registros Hoje</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{stats?.records_today || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Companies */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Empresas Recentes</CardTitle>
          <CardDescription>Últimas empresas cadastradas no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recent_companies?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_companies.map((company, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-ciaponto-cyan" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{company.name}</p>
                      <p className="text-sm text-slate-500">{company.email}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    company.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {company.is_active ? "Ativo" : "Inativo"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">Nenhuma empresa cadastrada</p>
          )}
        </CardContent>
      </Card>
      
      {/* Modal de Lista de Funcionários */}
      {showEmployeesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[#1CA1BA] to-[#17889E] text-white">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Todos os Funcionários
                  </CardTitle>
                  <CardDescription className="text-[#1CA1BA]/80">
                    {allEmployees.length} funcionários cadastrados no sistema
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowEmployeesModal(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-0">
              {/* Barra de busca */}
              <div className="p-4 border-b bg-slate-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nome, CPF ou empresa..."
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Lista de funcionários */}
              <div className="overflow-y-auto max-h-[calc(90vh-250px)]">
                {loadingEmployees ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
                  </div>
                ) : filteredEmployees.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {filteredEmployees.map((employee) => (
                      <div 
                        key={employee.id}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4"
                        onClick={() => openEmployeeDetails(employee)}
                      >
                        {/* Foto do funcionário */}
                        <div className="flex-shrink-0">
                          {employee.profile_photo_url ? (
                            <img 
                              src={employee.profile_photo_url} 
                              alt={employee.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-[#1CA1BA]/15 flex items-center justify-center">
                              <Users className="w-6 h-6 text-[#1CA1BA]" />
                            </div>
                          )}
                        </div>
                        
                        {/* Informações básicas */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{employee.name}</p>
                          <p className="text-sm text-slate-500 truncate">{employee.company_name || "Sem empresa"}</p>
                          {employee.cpf && (
                            <p className="text-xs text-slate-400">CPF: {employee.cpf}</p>
                          )}
                        </div>
                        
                        {/* Status */}
                        <div className="flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            employee.is_active !== false 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}>
                            {employee.is_active !== false ? "Ativo" : "Inativo"}
                          </span>
                        </div>
                        
                        <Eye className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhum funcionário encontrado</p>
                  </div>
                )}
              </div>
            </CardContent>
            
            <div className="p-4 border-t bg-slate-50">
              <Button variant="outline" onClick={() => setShowEmployeesModal(false)} className="w-full">
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Modal de Detalhes do Funcionário */}
      {showEmployeeDetails && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white relative">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowEmployeeDetails(false)}
                className="absolute right-4 top-4 text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
              
              {/* Foto grande clicável */}
              <div className="flex flex-col items-center pt-2">
                <div 
                  className="cursor-pointer transform hover:scale-105 transition-transform"
                  onClick={() => selectedEmployee.profile_photo_url && setShowPhotoModal(true)}
                >
                  {selectedEmployee.profile_photo_url ? (
                    <img 
                      src={selectedEmployee.profile_photo_url} 
                      alt={selectedEmployee.name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-slate-600 flex items-center justify-center border-4 border-white shadow-lg">
                      <Users className="w-12 h-12 text-slate-400" />
                    </div>
                  )}
                  {selectedEmployee.profile_photo_url && (
                    <p className="text-xs text-slate-300 text-center mt-2">Clique para ampliar</p>
                  )}
                </div>
                <CardTitle className="text-white text-center mt-3">{selectedEmployee.name}</CardTitle>
                <CardDescription className="text-slate-300 text-center">
                  {selectedEmployee.company_name || "Sem empresa"}
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 space-y-4">
              {/* Informações pessoais */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">CPF</Label>
                  <p className="font-medium text-slate-900">{selectedEmployee.cpf || "Não informado"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Telefone</Label>
                  <p className="font-medium text-slate-900">{selectedEmployee.phone || "Não informado"}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <p className="font-medium text-slate-900">{selectedEmployee.email || "Não informado"}</p>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Endereço</Label>
                <p className="font-medium text-slate-900">{selectedEmployee.address || "Não informado"}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Cargo</Label>
                  <p className="font-medium text-slate-900">{selectedEmployee.position || "Não informado"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Tipo de Escala</Label>
                  <p className="font-medium text-slate-900">
                    {selectedEmployee.work_schedule_type === "44h_semana_comercial" 
                      ? "44h (Comercial)" 
                      : selectedEmployee.work_schedule_type === "escala_6x1"
                        ? "Escala 6x1"
                        : selectedEmployee.work_schedule_type || "Padrão"}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Data de Admissão</Label>
                  <p className="font-medium text-slate-900">
                    {selectedEmployee.admission_date 
                      ? new Date(selectedEmployee.admission_date).toLocaleDateString("pt-BR")
                      : "Não informado"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedEmployee.is_active !== false 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    {selectedEmployee.is_active !== false ? "Ativo" : "Inativo"}
                  </span>
                </div>
              </div>
              
              {selectedEmployee.pis && (
                <div>
                  <Label className="text-xs text-slate-500">PIS</Label>
                  <p className="font-medium text-slate-900">{selectedEmployee.pis}</p>
                </div>
              )}
            </CardContent>
            
            <div className="p-4 border-t bg-slate-50">
              <Button variant="outline" onClick={() => setShowEmployeeDetails(false)} className="w-full">
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Modal de Foto Ampliada */}
      {showPhotoModal && selectedEmployee?.profile_photo_url && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] p-4 cursor-pointer"
          onClick={() => setShowPhotoModal(false)}
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowPhotoModal(false)}
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
            >
              <X className="w-6 h-6" />
              <span className="ml-2">Fechar</span>
            </Button>
            <img 
              src={selectedEmployee.profile_photo_url} 
              alt={selectedEmployee.name}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-4 text-lg font-medium">{selectedEmployee.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Companies Management
function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [companyEditData, setCompanyEditData] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    trade_name: "",
    cnpj: "",
    inscricao_estadual: "",
    inscricao_municipal: "",
    email: "",
    phone: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    plan: "basic",
    max_employees: 10,
    // Campos de endereço para NFS-e
    cep: "",
    address: "",
    number: "",
    bairro: "",
    city: "",
    state: "CE"
  });

  const plans = [
    { value: "basico", label: "Básico (R$ 159/mês)", employees: 5, price: "R$ 159" },
    { value: "profissional", label: "Profissional (R$ 299/mês)", employees: 10, price: "R$ 299" },
    { value: "enterprise", label: "Enterprise (R$ 499/mês)", employees: 30, price: "R$ 499" }
  ];

  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", 
    "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/companies`, formData);
      toast.success("Empresa cadastrada com sucesso!");
      setDialogOpen(false);
      setFormData({
        name: "", trade_name: "", cnpj: "", inscricao_estadual: "", inscricao_municipal: "",
        email: "", phone: "", admin_name: "", admin_email: "", admin_password: "",
        plan: "basic", max_employees: 10,
        cep: "", address: "", number: "", bairro: "", city: "", state: "CE"
      });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar empresa");
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Tem certeza que deseja desativar esta empresa?")) return;
    try {
      await axios.delete(`${API}/superadmin/companies/${id}`);
      toast.success("Empresa desativada");
      fetchCompanies();
    } catch (error) {
      toast.error("Erro ao desativar empresa");
    }
  };

  const handleEditPlan = (company) => {
    setEditingCompany({
      id: company.id,
      name: company.name,
      plan: company.plan || "basic",
      max_employees: company.max_employees || 10
    });
    setEditDialogOpen(true);
  };

  const handleSavePlan = async () => {
    if (!editingCompany) return;
    try {
      await axios.put(`${API}/superadmin/companies/${editingCompany.id}`, {
        plan: editingCompany.plan,
        max_employees: editingCompany.max_employees
      });
      toast.success(`Plano da empresa "${editingCompany.name}" atualizado com sucesso!`);
      setEditDialogOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar plano");
    }
  };

  const handleSetPaymentStatus = async (companyId, status) => {
    try {
      await axios.put(`${API}/superadmin/companies/${companyId}`, {
        payment_status: status
      });
      if (status === "paid") {
        toast.success("Empresa liberada! O acesso foi ativado.");
      } else {
        toast.info("Pagamento removido. A empresa voltará ao período de teste.");
      }
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  // Abrir modal de detalhes da empresa
  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setCompanyEditData({
      name: company.name || "",
      trade_name: company.trade_name || "",
      cnpj: company.cnpj || "",
      inscricao_estadual: company.inscricao_estadual || "",
      inscricao_municipal: company.inscricao_municipal || "",
      email: company.email || "",
      phone: company.phone || "",
      address: company.address || "",
      number: company.number || "",
      complement: company.complement || "",
      bairro: company.bairro || "",
      city: company.city || "",
      state: company.state || "",
      cep: company.cep || "",
      plan: company.plan || "basic",
      max_employees: company.max_employees || 10,
      admin_name: company.admin_name || "",
      admin_email: company.admin_email || "",
      admin_password: "", // Deixar vazio - só preenche se quiser alterar
      is_active: company.is_active !== false,
      payment_status: company.payment_status || "pending",
      trial_expires_at: company.trial_expires_at || ""
    });
    setEditMode(false);
    setDetailsDialogOpen(true);
  };

  // Salvar alterações da empresa
  const handleSaveCompanyDetails = async () => {
    if (!selectedCompany) return;
    
    setSavingCompany(true);
    try {
      const updateData = { ...companyEditData };
      
      // Se a senha estiver vazia, não enviar
      if (!updateData.admin_password) {
        delete updateData.admin_password;
      }
      
      await axios.put(`${API}/superadmin/companies/${selectedCompany.id}/full`, updateData);
      toast.success("Dados da empresa atualizados com sucesso!");
      setEditMode(false);
      setDetailsDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar empresa");
    } finally {
      setSavingCompany(false);
    }
  };

  // Reativar empresa
  const handleReactivateCompany = async (companyId) => {
    if (!window.confirm("Deseja reativar esta empresa?")) return;
    try {
      await axios.put(`${API}/superadmin/companies/${companyId}`, { is_active: true });
      toast.success("Empresa reativada com sucesso!");
      fetchCompanies();
      if (selectedCompany && selectedCompany.id === companyId) {
        setSelectedCompany({ ...selectedCompany, is_active: true });
        setCompanyEditData({ ...companyEditData, is_active: true });
      }
    } catch (error) {
      toast.error("Erro ao reativar empresa");
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Empresas/Clientes</h1>
          <p className="text-slate-600">Gerencie as empresas que contrataram o sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" data-testid="add-company-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Cadastrar Nova Empresa</DialogTitle>
              <DialogDescription>
                Preencha os dados da empresa e do administrador.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="border-b pb-4">
                <h4 className="font-medium text-slate-900 mb-3">Dados da Empresa</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Razão Social *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input
                        value={formData.trade_name}
                        onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                        placeholder="Nome comercial"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>CNPJ *</Label>
                      <Input
                        value={formData.cnpj}
                        onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                        placeholder="00.000.000/0000-00"
                        required
                      />
                    </div>
                    <div>
                      <Label>Inscrição Estadual</Label>
                      <Input
                        value={formData.inscricao_estadual}
                        onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                        placeholder="Isento ou número"
                      />
                    </div>
                    <div>
                      <Label>Inscrição Municipal</Label>
                      <Input
                        value={formData.inscricao_municipal}
                        onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                        placeholder="Número da IM"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Telefone *</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Email da Empresa *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-slate-900 mb-3">Endereço (para NFS-e)</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>CEP</Label>
                      <Input
                        value={formData.cep}
                        onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                        placeholder="00000-000"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Logradouro</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Rua, Avenida, etc."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label>Número</Label>
                      <Input
                        value={formData.number}
                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                        placeholder="Nº"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label>Bairro</Label>
                      <Input
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label>Cidade</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>UF</Label>
                      <select
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      >
                        {estados.map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-slate-900 mb-3">Administrador da Empresa</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Nome do Admin</Label>
                    <Input
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Email do Admin</Label>
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Senha Inicial</Label>
                    <Input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-3">Plano</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Plano Contratado</Label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={formData.plan}
                      onChange={(e) => {
                        const plan = plans.find(p => p.value === e.target.value);
                        setFormData({ 
                          ...formData, 
                          plan: e.target.value,
                          max_employees: plan?.employees || 10
                        });
                      }}
                    >
                      {plans.map(plan => (
                        <option key={plan.value} value={plan.value}>{plan.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Máx. Funcionários</Label>
                    <Input
                      type="number"
                      value={formData.max_employees}
                      onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" data-testid="submit-company-btn">
                  Cadastrar Empresa
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Buscar empresa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Companies List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : filteredCompanies.length > 0 ? (
        <div className="grid gap-4">
          {filteredCompanies.map((company) => (
            <Card key={company.id} className="border border-slate-200" data-testid={`company-card-${company.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {company.logo_url ? (
                      <img 
                        src={company.logo_url} 
                        alt={company.name} 
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-ciaponto-cyan" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{company.name}</p>
                      <p className="text-sm text-slate-500">{company.email} • {company.cnpj || "Sem CNPJ"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="hidden sm:flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{company.employee_count || 0}</p>
                        <p className="text-slate-500">Funcionários</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{company.max_employees}</p>
                        <p className="text-slate-500">Limite</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      company.plan === "enterprise" ? "bg-purple-100 text-purple-700" :
                      company.plan === "professional" ? "bg-cyan-100 text-ciaponto-navy" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {company.plan === "enterprise" ? "Enterprise" :
                       company.plan === "professional" ? "Profissional" : "Básico"}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      company.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                    }`}>
                      {company.payment_status === "paid" ? "💰 Pago" : "⏳ Teste"}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      company.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}>
                      {company.is_active ? "Ativo" : "Inativo"}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(company)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Detalhes / Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditPlan(company)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Alterar Plano
                        </DropdownMenuItem>
                        {company.payment_status !== "paid" ? (
                          <DropdownMenuItem onClick={() => handleSetPaymentStatus(company.id, "paid")} className="text-green-600">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Marcar como Pago
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleSetPaymentStatus(company.id, "pending")} className="text-orange-600">
                            <Clock className="w-4 h-4 mr-2" />
                            Remover Pagamento
                          </DropdownMenuItem>
                        )}
                        {company.is_active ? (
                          <DropdownMenuItem onClick={() => handleDeactivate(company.id)} className="text-red-600">
                            <XCircle className="w-4 h-4 mr-2" />
                            Desativar
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleReactivateCompany(company.id)} className="text-green-600">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Reativar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nenhuma empresa cadastrada</p>
            <p className="text-sm text-slate-500">Clique em &quot;Nova Empresa&quot; para começar</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Edição de Plano */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Alterar Plano</DialogTitle>
            <DialogDescription>
              Altere o plano da empresa {editingCompany?.name}
            </DialogDescription>
          </DialogHeader>
          {editingCompany && (
            <div className="space-y-6">
              <div className="space-y-3">
                {plans.map((plan) => (
                  <div
                    key={plan.value}
                    onClick={() => setEditingCompany({
                      ...editingCompany,
                      plan: plan.value,
                      max_employees: plan.employees
                    })}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      editingCompany.plan === plan.value
                        ? "border-[#1CA1BA] bg-sky-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {plan.value === "basico" ? "Básico" : 
                           plan.value === "profissional" ? "Profissional" : "Enterprise"}
                        </p>
                        <p className="text-sm text-slate-500">
                          Até {plan.employees} funcionários
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900">{plan.price}</p>
                        <p className="text-xs text-slate-500">/mês</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)} 
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSavePlan} 
                  className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                >
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes/Edição Completa da Empresa */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editMode ? "Editar Empresa" : "Detalhes da Empresa"}
            </DialogTitle>
            <DialogDescription>
              {editMode ? "Edite os dados da empresa" : "Visualize e gerencie todas as informações da empresa"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-6">
              {/* Botões de ação */}
              <div className="flex justify-end gap-2">
                {!editMode ? (
                  <Button onClick={() => setEditMode(true)} className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar Dados
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setEditMode(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleSaveCompanyDetails} 
                      disabled={savingCompany}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      {savingCompany ? "Salvando..." : "Salvar Alterações"}
                    </Button>
                  </>
                )}
              </div>

              {/* Status da Empresa */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  companyEditData.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                }`}>
                  {companyEditData.is_active ? "✅ Ativa" : "❌ Inativa"}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  companyEditData.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                }`}>
                  {companyEditData.payment_status === "paid" ? "💰 Pago" : "⏳ Período de Teste"}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  companyEditData.plan === "enterprise" ? "bg-purple-100 text-purple-700" :
                  companyEditData.plan === "profissional" ? "bg-cyan-100 text-ciaponto-navy" :
                  "bg-slate-100 text-slate-700"
                }`}>
                  Plano: {companyEditData.plan === "enterprise" ? "Enterprise" :
                   companyEditData.plan === "profissional" ? "Profissional" : "Básico"}
                </span>
              </div>

              {/* Dados da Empresa */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Dados da Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Razão Social</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.name}
                        onChange={(e) => setCompanyEditData({...companyEditData, name: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.name || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Fantasia</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.trade_name}
                        onChange={(e) => setCompanyEditData({...companyEditData, trade_name: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.trade_name || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>CNPJ</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.cnpj}
                        onChange={(e) => setCompanyEditData({...companyEditData, cnpj: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.cnpj || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Inscrição Estadual</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.inscricao_estadual || ""}
                        onChange={(e) => setCompanyEditData({...companyEditData, inscricao_estadual: e.target.value})}
                        placeholder="Isento ou número"
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.inscricao_estadual || "Não informado"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Inscrição Municipal</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.inscricao_municipal || ""}
                        onChange={(e) => setCompanyEditData({...companyEditData, inscricao_municipal: e.target.value})}
                        placeholder="Número da IM"
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.inscricao_municipal || "Não informado"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Email da Empresa</Label>
                    {editMode ? (
                      <Input
                        type="email"
                        value={companyEditData.email}
                        onChange={(e) => setCompanyEditData({...companyEditData, email: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.email || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.phone}
                        onChange={(e) => setCompanyEditData({...companyEditData, phone: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.phone || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-slate-900">📍 Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Logradouro</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.address}
                        onChange={(e) => setCompanyEditData({...companyEditData, address: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.address || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Número</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.number}
                        onChange={(e) => setCompanyEditData({...companyEditData, number: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.number || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Complemento</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.complement}
                        onChange={(e) => setCompanyEditData({...companyEditData, complement: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.complement || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Bairro</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.bairro}
                        onChange={(e) => setCompanyEditData({...companyEditData, bairro: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.bairro || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.city}
                        onChange={(e) => setCompanyEditData({...companyEditData, city: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.city || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.state}
                        onChange={(e) => setCompanyEditData({...companyEditData, state: e.target.value})}
                        maxLength={2}
                        placeholder="UF"
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.state || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.cep}
                        onChange={(e) => setCompanyEditData({...companyEditData, cep: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.cep || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Plano e Configurações */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-slate-900">⚙️ Plano e Configurações</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Plano</Label>
                    {editMode ? (
                      <select
                        value={companyEditData.plan}
                        onChange={(e) => {
                          const plan = e.target.value;
                          const maxEmp = plan === "enterprise" ? 30 : plan === "profissional" ? 10 : 5;
                          setCompanyEditData({...companyEditData, plan, max_employees: maxEmp});
                        }}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="basico">Básico (R$ 159/mês)</option>
                        <option value="profissional">Profissional (R$ 299/mês)</option>
                        <option value="enterprise">Enterprise (R$ 499/mês)</option>
                      </select>
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">
                        {companyEditData.plan === "enterprise" ? "Enterprise" :
                         companyEditData.plan === "profissional" ? "Profissional" : "Básico"}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Limite de Funcionários</Label>
                    {editMode ? (
                      <Input
                        type="number"
                        value={companyEditData.max_employees}
                        onChange={(e) => setCompanyEditData({...companyEditData, max_employees: parseInt(e.target.value)})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.max_employees}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Status Pagamento</Label>
                    {editMode ? (
                      <select
                        value={companyEditData.payment_status}
                        onChange={(e) => setCompanyEditData({...companyEditData, payment_status: e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="pending">Período de Teste</option>
                        <option value="paid">Pago</option>
                      </select>
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">
                        {companyEditData.payment_status === "paid" ? "Pago" : "Período de Teste"}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Status da Empresa</Label>
                    {editMode ? (
                      <select
                        value={companyEditData.is_active ? "true" : "false"}
                        onChange={(e) => setCompanyEditData({...companyEditData, is_active: e.target.value === "true"})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="true">Ativa</option>
                        <option value="false">Inativa</option>
                      </select>
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">
                        {companyEditData.is_active ? "Ativa" : "Inativa"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Administrador da Empresa */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-slate-900">👤 Administrador da Empresa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome do Admin</Label>
                    {editMode ? (
                      <Input
                        value={companyEditData.admin_name}
                        onChange={(e) => setCompanyEditData({...companyEditData, admin_name: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.admin_name || "-"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Email do Admin (Login)</Label>
                    {editMode ? (
                      <Input
                        type="email"
                        value={companyEditData.admin_email}
                        onChange={(e) => setCompanyEditData({...companyEditData, admin_email: e.target.value})}
                      />
                    ) : (
                      <p className="text-slate-600 bg-slate-50 p-2 rounded">{companyEditData.admin_email || "-"}</p>
                    )}
                  </div>
                  {editMode && (
                    <div className="space-y-2 md:col-span-2">
                      <Label>Nova Senha (deixe vazio para manter a atual)</Label>
                      <Input
                        type="password"
                        value={companyEditData.admin_password}
                        onChange={(e) => setCompanyEditData({...companyEditData, admin_password: e.target.value})}
                        placeholder="Digite apenas se quiser alterar a senha"
                      />
                      <p className="text-xs text-slate-500">A senha só será alterada se você preencher este campo</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Informações do Sistema */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <h3 className="font-medium text-slate-900">📊 Informações do Sistema</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">ID</p>
                    <p className="font-mono text-xs break-all">{selectedCompany.id}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Funcionários</p>
                    <p className="font-medium">{selectedCompany.employee_count || 0} / {companyEditData.max_employees}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Cadastrado em</p>
                    <p className="font-medium">
                      {selectedCompany.created_at ? new Date(selectedCompany.created_at).toLocaleDateString('pt-BR') : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Teste expira em</p>
                    <p className="font-medium">
                      {selectedCompany.trial_expires_at ? new Date(selectedCompany.trial_expires_at).toLocaleDateString('pt-BR') : "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Financial Dashboard Component (Super Admin)
function FinanceiroDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  
  // Estados para modais de listagem
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [invoicesModalTitle, setInvoicesModalTitle] = useState("");
  const [invoicesModalFilter, setInvoicesModalFilter] = useState("all");
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  
  // Estados para modal de boletos por empresa
  const [showCompanyBoletosModal, setShowCompanyBoletosModal] = useState(false);
  const [selectedCompanyBoletos, setSelectedCompanyBoletos] = useState(null);
  const [selectedBoletos, setSelectedBoletos] = useState([]);
  const [cancelingBoletos, setCancelingBoletos] = useState(false);
  const [companyBoletosFilter, setCompanyBoletosFilter] = useState("all");
  
  // Estados para arquivar carnês
  const [archivedCompanies, setArchivedCompanies] = useState([]);
  const [archivingCompany, setArchivingCompany] = useState(null);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  // Estado para emissao de NFS-e direto da tela de boletos
  const [enviandoSpeedGov, setEnviandoSpeedGov] = useState(null);
  
  // Form state for generating invoices
  const [generateForm, setGenerateForm] = useState({
    company_id: "",
    plan_type: "basico",
    num_installments: 12,
    due_day: 5,
    first_due_date: "",
    custom_amount: "",
    payer_name: "",
    payer_cnpj: "",
    payer_email: "",
    payment_method: "boleto",  // Sempre boleto (já inclui QRCode PIX)
    notes: ""
  });

  useEffect(() => {
    fetchDashboard();
    fetchCompanies();
    fetchArchivedCompanies();
    // Set default first due date to next month day 5
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(5);
    setGenerateForm(prev => ({
      ...prev,
      first_due_date: nextMonth.toISOString().split("T")[0]
    }));
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchArchivedCompanies = async () => {
    try {
      const response = await axios.get(`${API}/invoices/archived-companies`);
      setArchivedCompanies(response.data || []);
    } catch (error) {
      console.error("Error fetching archived:", error);
    }
  };

  const handleArchiveCompany = async (companyId, companyName) => {
    if (!window.confirm(`Deseja arquivar o carnê de "${companyName}"?\n\nIsso irá ocultar o carnê da lista ativa. A receita total não será afetada.`)) return;
    setArchivingCompany(companyId);
    try {
      const response = await axios.post(`${API}/invoices/archive-company/${companyId}`);
      toast.success(response.data.message);
      await fetchDashboard();
      await fetchArchivedCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao arquivar carnê");
    } finally {
      setArchivingCompany(null);
    }
  };

  const handleRestoreCompany = async (companyId) => {
    if (!window.confirm("Deseja restaurar este carnê?")) return;
    try {
      await axios.post(`${API}/invoices/restore-company/${companyId}`);
      toast.success("Carnê restaurado com sucesso!");
      await fetchDashboard();
      await fetchArchivedCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao restaurar carnê");
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, invRes] = await Promise.all([
        axios.get(`${API}/admin/financial/dashboard`),
        axios.get(`${API}/admin/financial/invoices`)
      ]);
      setDashboard(dashRes.data);
      setAllInvoices(invRes.data.invoices);
    } catch (error) {
      console.error("Error fetching financial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/admin/invoices/${invoiceId}/mark-paid`);
      toast.success("Fatura marcada como paga!");
      fetchDashboard();
    } catch (error) {
      toast.error("Erro ao marcar como paga");
    }
  };

  const handleGenerateInvoices = async () => {
    if (!generateForm.company_id) {
      toast.error("Selecione uma empresa");
      return;
    }
    if (!generateForm.payer_name || !generateForm.payer_cnpj) {
      toast.error("Preencha nome/razão social e CNPJ do pagador");
      return;
    }
    
    setGenerating(true);
    try {
      const payload = {
        ...generateForm,
        custom_amount: generateForm.custom_amount ? parseFloat(generateForm.custom_amount) : null
      };
      
      const response = await axios.post(`${API}/admin/generate-invoices`, payload);
      setGeneratedResult(response.data);
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar carnê");
    } finally {
      setGenerating(false);
    }
  };

  const resetGenerateForm = () => {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(5);
    setGenerateForm({
      company_id: "",
      plan_type: "basico",
      num_installments: 12,
      due_day: 5,
      first_due_date: nextMonth.toISOString().split("T")[0],
      custom_amount: "",
      payer_name: "",
      payer_cnpj: "",
      payer_email: "",
      payment_method: "boleto",  // Sempre boleto
      notes: ""
    });
    setGeneratedResult(null);
  };

  // Deletar fatura
  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta fatura? Esta ação também cancelará o boleto no EFI Bank se existir.")) {
      return;
    }
    
    setDeletingInvoice(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/cancel`);
      toast.success("Fatura cancelada com sucesso!");
      
      // Remover a fatura da lista local (não aparece mais)
      setAllInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      
      // Atualizar o dashboard para recalcular os totais
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar fatura");
    } finally {
      setDeletingInvoice(null);
    }
  };

  // ==================== BOLETOS POR EMPRESA ====================
  
  // Abrir modal de boletos de uma empresa
  const openCompanyBoletosModal = async (companyId, companyData) => {
    // Marcar quais invoices já têm NFS-e oficial emitida
    let invoicesEnriched = companyData.invoices || [];
    try {
      const nfseRes = await axios.get(`${API}/nfse/oficial`);
      const nfseList = nfseRes.data || [];
      const nfseMap = {};
      nfseList.forEach(n => {
        if (n.invoice_id && n.numero_nfse_oficial) {
          nfseMap[n.invoice_id] = n.numero_nfse_oficial;
        }
      });
      invoicesEnriched = invoicesEnriched.map(inv => ({
        ...inv,
        has_nfse_oficial: !!nfseMap[inv.id],
        numero_nfse_oficial: nfseMap[inv.id] || null
      }));
    } catch (e) {
      // Se falhar busca de nfse_oficial, segue com dados originais
    }

    setSelectedCompanyBoletos({
      companyId,
      companyName: companyData.company_name,
      phone: companyData.phone || companyData.admin_phone || "",
      email: companyData.email || "",
      invoices: invoicesEnriched
    });
    setSelectedBoletos([]);
    setCompanyBoletosFilter("all");
    setShowCompanyBoletosModal(true);
  };

  // Emitir NFS-e direto da tela de boletos (para boleto PAGO)
  const handleEmitirNotaBoleto = async (invoice) => {
    if (invoice.status !== "paid") {
      toast.error("Só é possível emitir nota fiscal para boletos pagos");
      return;
    }
    if (invoice.has_nfse_oficial) {
      toast.info(`Esta fatura já possui NFS-e nº ${invoice.numero_nfse_oficial} emitida`);
      return;
    }
    const confirmado = window.confirm(
      `Emitir NFS-e oficial para o boleto ${invoice.invoice_number}/12?\n\n` +
      `Empresa: ${selectedCompanyBoletos?.companyName}\n` +
      `Valor: R$ ${invoice.amount?.toFixed(2)}\n\n` +
      `Ambiente: PRODUCAO (Prefeitura de Iguatu-CE)`
    );
    if (!confirmado) return;

    try {
      setEnviandoSpeedGov(invoice.id);
      toast.info("Enviando ao SpeedGov... aguarde");
      const response = await axios.post(`${API}/nfse/emitir-oficial`, {
        invoice_id: invoice.id,
        ambiente: "producao"
      });
      if (response.data.success) {
        const proto = response.data.protocolo;
        const nfseNum = response.data.numero_nfse;
        if (nfseNum) {
          toast.success(`NFS-e #${nfseNum} emitida com sucesso!`, { duration: 8000 });
        } else if (proto) {
          toast.success(`Lote enviado! Protocolo: ${proto}. A nota será processada em alguns minutos.`, { duration: 10000 });
        }
        setSelectedCompanyBoletos(prev => prev ? {
          ...prev,
          invoices: prev.invoices.map(inv =>
            inv.id === invoice.id
              ? { ...inv, has_nfse_oficial: true, numero_nfse_oficial: nfseNum || "..." }
              : inv
          )
        } : null);
      } else {
        toast.error(response.data.detail || "Erro no envio ao SpeedGov");
      }
    } catch (error) {
      console.error("Erro ao emitir NFS-e do boleto:", error);
      const detail = error.response?.data?.detail || "Erro ao comunicar com SpeedGov";
      toast.error(detail, { duration: 8000 });
    } finally {
      setEnviandoSpeedGov(null);
    }
  };
  
  // Filtrar boletos da empresa selecionada
  const getFilteredCompanyBoletos = () => {
    if (!selectedCompanyBoletos) return [];
    
    const today = new Date().toISOString().split('T')[0];
    let filtered = selectedCompanyBoletos.invoices;
    
    switch (companyBoletosFilter) {
      case "pending":
        filtered = filtered.filter(inv => inv.status === "pending" && inv.due_date >= today);
        break;
      case "overdue":
        filtered = filtered.filter(inv => 
          inv.status === "overdue" || (inv.status === "pending" && inv.due_date < today)
        );
        break;
      case "paid":
        filtered = filtered.filter(inv => inv.status === "paid");
        break;
      case "cancelled":
        filtered = filtered.filter(inv => inv.status === "cancelled");
        break;
      default:
        // "all" - mantém todos
        break;
    }
    
    return filtered.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };
  
  // Toggle seleção de boleto individual
  const toggleBoletoSelection = (invoiceId) => {
    setSelectedBoletos(prev => 
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };
  
  // Selecionar todos os boletos visíveis (não pagos/cancelados)
  const selectAllVisibleBoletos = () => {
    const visibleBoletos = getFilteredCompanyBoletos()
      .filter(inv => inv.status !== "paid" && inv.status !== "cancelled")
      .map(inv => inv.id);
    
    if (selectedBoletos.length === visibleBoletos.length) {
      setSelectedBoletos([]);
    } else {
      setSelectedBoletos(visibleBoletos);
    }
  };
  
  // Cancelar boletos selecionados
  const handleCancelSelectedBoletos = async () => {
    if (selectedBoletos.length === 0) {
      toast.error("Selecione pelo menos um boleto para cancelar");
      return;
    }
    
    const confirmMsg = `Tem certeza que deseja cancelar ${selectedBoletos.length} boleto(s)?\n\nEsta ação também cancelará os boletos no EFI Bank e não pode ser desfeita.`;
    if (!window.confirm(confirmMsg)) return;
    
    setCancelingBoletos(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const invoiceId of selectedBoletos) {
      try {
        await axios.post(`${API}/invoices/${invoiceId}/cancel`);
        successCount++;
        
        // Atualizar na lista geral
        setAllInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        
        // Atualizar no modal
        setSelectedCompanyBoletos(prev => ({
          ...prev,
          invoices: prev.invoices.filter(inv => inv.id !== invoiceId)
        }));
      } catch (error) {
        console.error(`Erro ao cancelar boleto ${invoiceId}:`, error);
        errorCount++;
      }
    }
    
    setCancelingBoletos(false);
    setSelectedBoletos([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} boleto(s) cancelado(s) com sucesso!`);
      fetchDashboard();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} boleto(s) não puderam ser cancelados`);
    }
  };
  
  // Cancelar um boleto individual do modal
  const handleCancelSingleBoleto = async (invoiceId) => {
    if (!window.confirm("Tem certeza que deseja cancelar este boleto?\n\nEsta ação também cancelará no EFI Bank.")) {
      return;
    }
    
    try {
      await axios.post(`${API}/invoices/${invoiceId}/cancel`);
      toast.success("Boleto cancelado com sucesso!");
      
      // Atualizar na lista geral
      setAllInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      
      // Atualizar no modal
      setSelectedCompanyBoletos(prev => ({
        ...prev,
        invoices: prev.invoices.filter(inv => inv.id !== invoiceId)
      }));
      
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar boleto");
    }
  };

  // Função para enviar cobrança via WhatsApp
  const sendWhatsAppCobranca = (invoice, companyData, isOverdue) => {
    // Dados da CiaPonto (empresa emissora)
    const controlISP = {
      nome: "CiaPonto",
      cnpj: "47.223.088/0001-74",
      telefone: "(88) 99947-1678",
      site: "www.control-isp.com.br"
    };
    
    // Formatar data de vencimento
    const dataVencimento = new Date(invoice.due_date).toLocaleDateString("pt-BR");
    
    // Calcular dias de atraso se vencido
    const diasAtraso = isOverdue 
      ? Math.floor((new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24))
      : Math.floor((new Date(invoice.due_date) - new Date()) / (1000 * 60 * 60 * 24));
    
    // Valor formatado
    const valorFormatado = invoice.amount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    
    // Nome da empresa cliente
    const nomeCliente = companyData?.name || companyData?.company_name || "Cliente";
    
    let mensagem = "";
    
    if (isOverdue) {
      // Mensagem para boleto VENCIDO
      mensagem = `🔔 *AVISO DE COBRANÇA - BOLETO VENCIDO*

Prezado(a) cliente *${nomeCliente}*,

Identificamos que existe um boleto em aberto referente à sua assinatura do sistema de ponto eletrônico CiaPonto.

📋 *Detalhes da Cobrança:*
━━━━━━━━━━━━━━━━━━━━━━
• Parcela: ${invoice.invoice_number || "N/A"}/12
• Vencimento: ${dataVencimento}
• Valor: ${valorFormatado}
• Situação: ⚠️ *VENCIDO há ${diasAtraso} dia(s)*
━━━━━━━━━━━━━━━━━━━━━━

📄 *Acesse seu boleto:*
${invoice.boleto_link || "Entre em contato para obter o boleto"}

💡 *Dica:* O boleto já inclui QR Code PIX para pagamento instantâneo!

⚠️ Para evitar a suspensão dos serviços, por favor regularize o pagamento o mais breve possível.

━━━━━━━━━━━━━━━━━━━━━━
🏢 *${controlISP.nome}*
📋 CNPJ: ${controlISP.cnpj}
📞 WhatsApp: ${controlISP.telefone}
🌐 ${controlISP.site}
━━━━━━━━━━━━━━━━━━━━━━

_✅ Caso já tenha efetuado o pagamento, por favor desconsidere esta mensagem. O reconhecimento pode levar até 3 dias úteis._

Agradecemos a compreensão! 🙏`;
    } else {
      // Mensagem para boleto PRÓXIMO DO VENCIMENTO (em dia)
      mensagem = `📢 *LEMBRETE DE VENCIMENTO*

Prezado(a) cliente *${nomeCliente}*,

Este é um lembrete amigável sobre o vencimento próximo da sua assinatura do sistema de ponto eletrônico CiaPonto.

📋 *Detalhes da Cobrança:*
━━━━━━━━━━━━━━━━━━━━━━
• Parcela: ${invoice.invoice_number || "N/A"}/12
• Vencimento: ${dataVencimento}
• Valor: ${valorFormatado}
• Situação: ⏳ Vence em ${diasAtraso} dia(s)
━━━━━━━━━━━━━━━━━━━━━━

📄 *Acesse seu boleto:*
${invoice.boleto_link || "Entre em contato para obter o boleto"}

💡 *Dica:* O boleto já inclui QR Code PIX para pagamento instantâneo e mais rápido!

✨ Evite juros e multas pagando até a data de vencimento.

━━━━━━━━━━━━━━━━━━━━━━
🏢 *${controlISP.nome}*
📋 CNPJ: ${controlISP.cnpj}
📞 WhatsApp: ${controlISP.telefone}
🌐 ${controlISP.site}
━━━━━━━━━━━━━━━━━━━━━━

_✅ Caso já tenha efetuado o pagamento, por favor desconsidere esta mensagem. O reconhecimento pode levar até 3 dias úteis._

Ficamos à disposição! 😊`;
    }
    
    // Obter telefone do cliente (da empresa)
    const telefoneCliente = companyData?.phone || "";
    
    // Limpar telefone (apenas números)
    const telefoneNumeros = telefoneCliente.replace(/\D/g, "");
    
    // Formatar para WhatsApp (adicionar 55 se não tiver)
    const telefoneWhatsApp = telefoneNumeros.startsWith("55") 
      ? telefoneNumeros 
      : `55${telefoneNumeros}`;
    
    // Codificar mensagem para URL
    const mensagemCodificada = encodeURIComponent(mensagem);
    
    // Verificar se tem telefone válido (mínimo 10 dígitos + 55 = 12)
    if (telefoneWhatsApp.length >= 12) {
      // Abrir WhatsApp diretamente no contato
      window.open(`https://wa.me/${telefoneWhatsApp}?text=${mensagemCodificada}`, "_blank");
      toast.success("WhatsApp aberto com a mensagem de cobrança!");
    } else {
      // Telefone não cadastrado - mostrar erro e opção de copiar
      toast.error(
        `Telefone não cadastrado para ${nomeCliente}. Cadastre o telefone da empresa primeiro.`,
        { duration: 5000 }
      );
      // Copiar mensagem para área de transferência
      navigator.clipboard.writeText(mensagem).then(() => {
        toast.info("Mensagem copiada! Cole no WhatsApp do cliente.", { duration: 4000 });
      });
    }
  };

  // Sincronizar status das faturas com o EFI Bank
  const [syncingInvoices, setSyncingInvoices] = useState(false);
  const [generatingBoleto, setGeneratingBoleto] = useState(null);
  
  // Gerar boleto individual para fatura existente
  const handleGenerateBoleto = async (invoiceId) => {
    setGeneratingBoleto(invoiceId);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/generate-boleto`);
      const data = response.data;
      
      if (data.success) {
        toast.success("✅ Boleto gerado com sucesso!");
        
        // Atualizar a fatura na lista local com os dados do boleto
        setAllInvoices(prev => prev.map(inv => 
          inv.id === invoiceId 
            ? { ...inv, boleto_link: data.boleto_link, charge_id: data.charge_id, barcode: data.barcode }
            : inv
        ));
      } else {
        toast.error(data.error || "Erro ao gerar boleto");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar boleto no EFI Bank");
    } finally {
      setGeneratingBoleto(null);
    }
  };
  
  const handleSyncInvoices = async () => {
    if (!window.confirm("Deseja sincronizar o status de todas as faturas pendentes com o EFI Bank?\n\nIsso irá verificar pagamentos e cancelamentos feitos diretamente no EFI.")) {
      return;
    }
    
    setSyncingInvoices(true);
    try {
      const response = await axios.post(`${API}/invoices/sync-status`);
      const data = response.data;
      
      // Mostrar mensagem apropriada baseada no resultado
      if (data.invoices_with_boleto === 0) {
        toast.info(data.message || "Nenhuma fatura com boleto para sincronizar.");
      } else if (data.payments_found > 0) {
        toast.success(`✅ ${data.message}`);
      } else {
        toast.success(`✅ ${data.message}`);
      }
      
      // Recarregar faturas e dashboard
      fetchDashboard();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao sincronizar faturas");
    } finally {
      setSyncingInvoices(false);
    }
  };

  // Abrir modal de faturas com filtro
  const openInvoicesModal = (filterType, title) => {
    setInvoicesModalFilter(filterType);
    setInvoicesModalTitle(title);
    setShowInvoicesModal(true);
  };

  // Faturas filtradas para o modal (excluindo canceladas)
  const getFilteredInvoicesForModal = () => {
    // Primeiro, excluir faturas canceladas de todas as listas
    const activeInvoices = allInvoices.filter(inv => inv.status !== "cancelled" && inv.status !== "canceled");
    const todayStr = new Date().toISOString().split("T")[0];
    
    if (invoicesModalFilter === "all") return activeInvoices;
    if (invoicesModalFilter === "paid") return activeInvoices.filter(inv => inv.status === "paid");
    if (invoicesModalFilter === "pending") return activeInvoices.filter(inv => inv.status === "pending");
    if (invoicesModalFilter === "overdue") return activeInvoices.filter(inv => inv.status === "overdue" || (inv.status === "pending" && inv.due_date < todayStr));
    // Faturas em aberto = pendentes + em atraso (não pagas)
    if (invoicesModalFilter === "open") return activeInvoices.filter(inv => inv.status === "pending" || inv.status === "overdue");
    return activeInvoices;
  };

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setGenerateForm(prev => ({
        ...prev,
        company_id: companyId,
        payer_email: company.email || ""
      }));
    }
  };

  const planPrices = { basico: 99, profissional: 299, enterprise: 599 };
  const selectedPlanPrice = generateForm.custom_amount 
    ? parseFloat(generateForm.custom_amount) 
    : planPrices[generateForm.plan_type];

  const getStatusBadge = (status, dueDate) => {
    const today = new Date().toISOString().split("T")[0];
    if (status === "paid") return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Pago</span>;
    if (status === "overdue" || (status === "pending" && dueDate < today)) return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Vencida</span>;
    if (status === "cancelled" || status === "canceled") return <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium line-through">Cancelada</span>;
    return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Pendente</span>;
  };

  const filteredInvoices = allInvoices.filter(inv => {
    // Primeiro, excluir faturas canceladas
    if (inv.status === "cancelled" || inv.status === "canceled") return false;
    
    if (filter === "all") return true;
    if (filter === "paid") return inv.status === "paid";
    if (filter === "pending") return inv.status === "pending";
    if (filter === "overdue") return inv.status === "overdue" || (inv.status === "pending" && inv.due_date < new Date().toISOString().split("T")[0]);
    return true;
  });

  // Gerar PDF do carnê de uma empresa
  const generateCarnePDF = (companyName, invoices) => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("CARNÊ DE PAGAMENTO", 105, 20, { align: "center" });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Empresa: ${companyName}`, 14, 35);
    doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")}`, 14, 42);
    
    const totalValue = invoices.reduce((sum, inv) => sum + inv.amount, 0);
    doc.text(`Total do Carnê: R$ ${totalValue.toFixed(2)}`, 14, 49);
    doc.text(`Parcelas: ${invoices.length}x`, 14, 56);
    
    // Tabela de parcelas
    autoTable(doc, {
      startY: 65,
      head: [["Parcela", "Vencimento", "Valor", "Status"]],
      body: invoices.map(inv => [
        `${inv.invoice_number}/${invoices.length}`,
        new Date(inv.due_date).toLocaleDateString("pt-BR"),
        `R$ ${inv.amount.toFixed(2)}`,
        inv.status === "paid" ? "Pago" : inv.status === "overdue" ? "Em Atraso" : "Pendente"
      ]),
      theme: "grid",
      headStyles: { fillColor: [14, 165, 233] },
      styles: { fontSize: 10 }
    });
    
    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `CiaPonto - Sistema de Ponto Eletrônico | Página ${i} de ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }
    
    doc.save(`carne_${companyName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Carnê baixado com sucesso!");
  };

  // Gerar PDF de uma fatura individual (boleto)
  const generateBoletoPDF = (invoice, companyName) => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFillColor(14, 165, 233);
    doc.rect(0, 0, 210, 40, "F");
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("FATURA", 105, 25, { align: "center" });
    
    // Dados da fatura
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    
    let y = 55;
    doc.text(`Empresa: ${companyName}`, 14, y);
    y += 10;
    doc.text(`Parcela: ${invoice.invoice_number}/12`, 14, y);
    y += 10;
    doc.text(`Vencimento: ${new Date(invoice.due_date).toLocaleDateString("pt-BR")}`, 14, y);
    y += 15;
    
    // Valor em destaque
    doc.setFillColor(240, 253, 244);
    doc.rect(14, y - 5, 180, 25, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Valor: R$ ${invoice.amount.toFixed(2)}`, 20, y + 10);
    y += 35;
    
    // Dados do pagador
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Dados do Pagador:", 14, y);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.text(`Razão Social: ${invoice.payer_name || "N/A"}`, 14, y);
    y += 7;
    doc.text(`CNPJ: ${invoice.payer_cnpj || invoice.payer_cpf || "N/A"}`, 14, y);
    y += 7;
    doc.text(`Email: ${invoice.payer_email || "N/A"}`, 14, y);
    y += 15;
    
    // Forma de pagamento
    doc.setFont("helvetica", "bold");
    doc.text(`Forma de Pagamento: BOLETO BANCÁRIO`, 14, y);
    y += 15;
    
    // Instruções
    doc.setFillColor(254, 249, 195);
    doc.rect(14, y - 5, 180, 35, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Instruções:", 20, y + 3);
    doc.text("• Pague esta fatura até a data de vencimento", 20, y + 12);
    doc.text("• Em caso de atraso, entre em contato com o suporte", 20, y + 20);
    doc.text("• Após o pagamento, o sistema será liberado automaticamente", 20, y + 28);
    
    // Rodapé
    doc.setFontSize(8);
    doc.text(
      "CiaPonto - Sistema de Ponto Eletrônico",
      105,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
    
    doc.save(`fatura_${companyName.replace(/\s+/g, "_")}_parcela${invoice.invoice_number}.pdf`);
    toast.success("Fatura baixada com sucesso!");
  };

  // Agrupar faturas por empresa para gerar carnê (excluindo canceladas e arquivadas)
  const invoicesByCompany = allInvoices
    .filter(inv => inv.status !== "cancelled" && inv.status !== "canceled" && !inv.archived)
    .reduce((acc, inv) => {
      const key = inv.company_id;
      if (!acc[key]) {
        acc[key] = {
          company_name: inv.company_name,
          phone: inv.company_phone || "",
          email: inv.company_email || "",
          invoices: []
        };
      }
      acc[key].invoices.push(inv);
      return acc;
    }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Dashboard Financeiro</h1>
          <p className="text-slate-600">Visão geral das receitas e cobranças</p>
        </div>
        <Button 
          onClick={() => { resetGenerateForm(); setShowGenerateModal(true); }}
          className="bg-green-600 hover:bg-green-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Gerar Carnê
        </Button>
        
        <Button 
          onClick={handleSyncInvoices}
          disabled={syncingInvoices}
          variant="outline"
          className="border-[#1CA1BA] text-ciaponto-cyan hover:bg-cyan-50"
        >
          {syncingInvoices ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1CA1BA] mr-2"></div>
              Sincronizando...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sincronizar EFI
            </>
          )}
        </Button>
      </div>

      {/* Modal para gerar carnê */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl">
                  {generatedResult ? "Carnê Gerado!" : "Gerar Carnê/Boletos"}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowGenerateModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <CardDescription>
                {generatedResult 
                  ? `${generatedResult.total_invoices} parcelas de R$ ${generatedResult.monthly_amount?.toFixed(2)}`
                  : "Gere faturas manualmente para uma empresa"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!generatedResult ? (
                <>
                  {/* Seleção de empresa */}
                  <div className="space-y-2">
                    <Label>Empresa *</Label>
                    <select
                      value={generateForm.company_id}
                      onChange={(e) => handleCompanySelect(e.target.value)}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="">Selecione uma empresa...</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Plano e valor */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Plano</Label>
                      <select
                        value={generateForm.plan_type}
                        onChange={(e) => setGenerateForm({...generateForm, plan_type: e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="basico">Básico - R$ 99/mês (7 func.)</option>
                        <option value="profissional">Profissional - R$ 299/mês (30 func.)</option>
                        <option value="enterprise">Enterprise - R$ 599/mês (ilimitado)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Valor Personalizado (opcional)</Label>
                      <Input
                        type="number"
                        placeholder="Ex: 150.00"
                        value={generateForm.custom_amount}
                        onChange={(e) => setGenerateForm({...generateForm, custom_amount: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Parcelas e vencimento */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Nº de Parcelas</Label>
                      <select
                        value={generateForm.num_installments}
                        onChange={(e) => setGenerateForm({...generateForm, num_installments: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-md"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12,18,24].map(n => (
                          <option key={n} value={n}>{n}x</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Dia de Vencimento</Label>
                      <select
                        value={generateForm.due_day}
                        onChange={(e) => setGenerateForm({...generateForm, due_day: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-md"
                      >
                        {Array.from({length: 28}, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>Dia {d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>1º Vencimento</Label>
                      <Input
                        type="date"
                        value={generateForm.first_due_date}
                        onChange={(e) => setGenerateForm({...generateForm, first_due_date: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Dados do pagador */}
                  <div className="pt-4 border-t">
                    <Label className="text-base font-medium">Dados do Pagador (Empresa)</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Razão Social *</Label>
                      <Input
                        value={generateForm.payer_name}
                        onChange={(e) => setGenerateForm({...generateForm, payer_name: e.target.value})}
                        placeholder="Razão Social da empresa"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CNPJ *</Label>
                      <Input
                        value={generateForm.payer_cnpj}
                        onChange={(e) => setGenerateForm({...generateForm, payer_cnpj: e.target.value})}
                        placeholder="00.000.000/0001-00"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={generateForm.payer_email}
                        onChange={(e) => setGenerateForm({...generateForm, payer_email: e.target.value})}
                        placeholder="email@empresa.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Forma de Pagamento</Label>
                      <div className="bg-green-50 p-3 rounded-md border border-green-200">
                        <p className="text-green-800 font-medium">📄 Boleto Bancário</p>
                        <p className="text-xs text-green-600">Inclui QR Code PIX para pagamento instantâneo</p>
                      </div>
                    </div>
                  </div>

                  {/* Observações */}
                  <div className="space-y-2">
                    <Label>Observações (opcional)</Label>
                    <Input
                      value={generateForm.notes}
                      onChange={(e) => setGenerateForm({...generateForm, notes: e.target.value})}
                      placeholder="Anotações internas"
                    />
                  </div>

                  {/* Resumo */}
                  <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                    <p className="text-sm text-ciaponto-navy font-medium">Resumo do Carnê:</p>
                    <p className="text-lg font-bold text-ciaponto-navy">
                      {generateForm.num_installments}x de R$ {selectedPlanPrice?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-ciaponto-cyan">
                      Total: R$ {(selectedPlanPrice * generateForm.num_installments)?.toFixed(2) || "0.00"}
                    </p>
                  </div>

                  {/* Botões */}
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" className="flex-1" onClick={() => setShowGenerateModal(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={handleGenerateInvoices}
                      disabled={generating}
                    >
                      {generating ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        "Gerar Carnê"
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Resultado da geração */}
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-lg font-medium text-slate-900">{generatedResult.company_name}</p>
                    <p className="text-slate-600">{generatedResult.plan_name}</p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-green-600">Parcelas</p>
                        <p className="font-bold text-green-800">{generatedResult.total_invoices}x de R$ {generatedResult.monthly_amount?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-green-600">Total</p>
                        <p className="font-bold text-green-800">R$ {generatedResult.total_value?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-green-600">1º Vencimento</p>
                        <p className="font-bold text-green-800">{new Date(generatedResult.first_due_date).toLocaleDateString("pt-BR")}</p>
                      </div>
                      <div>
                        <p className="text-green-600">Dia de Vencimento</p>
                        <p className="font-bold text-green-800">Todo dia {generatedResult.due_day}</p>
                      </div>
                    </div>
                  </div>

                  {/* Lista de faturas geradas */}
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Parcela</th>
                          <th className="p-2 text-left">Vencimento</th>
                          <th className="p-2 text-right">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generatedResult.invoices?.map((inv, idx) => (
                          <tr key={inv.id} className="border-t">
                            <td className="p-2">{inv.invoice_number}/{generatedResult.total_invoices}</td>
                            <td className="p-2">{new Date(inv.due_date).toLocaleDateString("pt-BR")}</td>
                            <td className="p-2 text-right">R$ {inv.amount?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Button 
                    className="w-full"
                    onClick={() => setShowGenerateModal(false)}
                  >
                    Fechar
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cards de resumo - Responsivo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        <Card className="border border-green-200 bg-green-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoicesModal("paid", "Receita Total (Faturas Pagas)")}>
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-green-600">Receita Total</p>
                <p className="font-bold text-sm sm:text-xl text-green-700">R$ {dashboard?.summary?.total_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <DollarSign className="w-5 h-5 sm:w-8 sm:h-8 text-green-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-sky-200 bg-sky-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoicesModal("all", "Receita do Mês")}>
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-ciaponto-cyan">Receita Mês</p>
                <p className="font-bold text-sm sm:text-xl text-ciaponto-navy">R$ {dashboard?.summary?.month_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <TrendingUp className="w-5 h-5 sm:w-8 sm:h-8 text-ciaponto-cyan hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-yellow-200 bg-yellow-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoicesModal("pending", "Faturas Pendentes")}>
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-yellow-600">Pendente</p>
                <p className="font-bold text-sm sm:text-xl text-yellow-700">R$ {dashboard?.summary?.pending_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <Clock className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-red-200 bg-red-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoicesModal("overdue", "Faturas em Atraso")}>
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-red-600">Em Atraso</p>
                <p className="font-bold text-sm sm:text-xl text-red-700">R$ {dashboard?.summary?.overdue_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-red-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-purple-200 bg-purple-50 cursor-pointer hover:shadow-md transition-shadow col-span-2 sm:col-span-1" onClick={() => openInvoicesModal("all", "Receita Futura")}>
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
              <div className="text-center sm:text-left">
                <p className="text-[10px] sm:text-xs text-purple-600">Receita Futura</p>
                <p className="font-bold text-sm sm:text-xl text-purple-700">R$ {dashboard?.summary?.future_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <BarChart3 className="w-5 h-5 sm:w-8 sm:h-8 text-purple-500 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contadores - Responsivo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card className="border border-sky-200 cursor-pointer hover:border-sky-400 hover:shadow-md transition-all" onClick={() => openInvoicesModal("open", "Faturas em Aberto")}>
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-ciaponto-cyan">{dashboard?.summary?.open_count || 0}</p>
            <p className="text-xs sm:text-sm text-slate-600">Em Aberto</p>
            <p className="text-[10px] sm:text-xs text-ciaponto-cyan mt-0.5 sm:mt-1">Ver lista</p>
          </CardContent>
        </Card>
        <Card className="border border-green-200 cursor-pointer hover:border-green-400 hover:shadow-md transition-all" onClick={() => openInvoicesModal("paid", "Faturas Pagas")}>
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{dashboard?.summary?.paid_count || 0}</p>
            <p className="text-xs sm:text-sm text-slate-600">Pagas</p>
            <p className="text-[10px] sm:text-xs text-ciaponto-cyan mt-0.5 sm:mt-1">Ver lista</p>
          </CardContent>
        </Card>
        <Card className="border border-yellow-200 cursor-pointer hover:border-yellow-400 hover:shadow-md transition-all" onClick={() => openInvoicesModal("pending", "Faturas Pendentes")}>
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-yellow-600">{dashboard?.summary?.pending_count || 0}</p>
            <p className="text-xs sm:text-sm text-slate-600">Pendentes</p>
            <p className="text-[10px] sm:text-xs text-ciaponto-cyan mt-0.5 sm:mt-1">Ver lista</p>
          </CardContent>
        </Card>
        <Card className="border border-red-200 cursor-pointer hover:border-red-400 hover:shadow-md transition-all" onClick={() => openInvoicesModal("overdue", "Faturas em Atraso")}>
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-red-600">{dashboard?.summary?.overdue_count || 0}</p>
            <p className="text-xs sm:text-sm text-slate-600">Em Atraso</p>
            <p className="text-[10px] sm:text-xs text-ciaponto-cyan mt-0.5 sm:mt-1">Ver lista</p>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Listagem de Faturas */}
      {showInvoicesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {invoicesModalTitle}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowInvoicesModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <CardDescription>
                {getFilteredInvoicesForModal().length} fatura(s) encontrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 max-h-[60vh] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Empresa</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Parcela</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Valor</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Vencimento</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredInvoicesForModal().map((invoice) => (
                    <tr key={invoice.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{invoice.company_name}</p>
                      </td>
                      <td className="p-3 text-slate-600">{invoice.invoice_number}/12</td>
                      <td className="p-3 font-medium text-slate-900">R$ {invoice.amount?.toFixed(2)}</td>
                      <td className="p-3 text-slate-600">{new Date(invoice.due_date).toLocaleDateString("pt-BR")}</td>
                      <td className="p-3">{getStatusBadge(invoice.status, invoice.due_date)}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {invoice.boleto_link ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open(invoice.boleto_link, "_blank")}
                              className="text-ciaponto-cyan border-sky-300 hover:bg-cyan-50"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Boleto
                            </Button>
                          ) : invoice.status === "pending" && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGenerateBoleto(invoice.id)}
                              disabled={generatingBoleto === invoice.id}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              title="Gerar boleto no EFI Bank"
                            >
                              {generatingBoleto === invoice.id ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-green-500 border-t-transparent mr-1"></div>
                              ) : (
                                <CreditCard className="w-3 h-3 mr-1" />
                              )}
                              Gerar Boleto
                            </Button>
                          )}
                          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              disabled={deletingInvoice === invoice.id}
                              title="Cancelar fatura"
                            >
                              {deletingInvoice === invoice.id ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-500 border-t-transparent"></div>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {getFilteredInvoicesForModal().length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nenhuma fatura encontrada
                </div>
              )}
            </CardContent>
            <div className="p-4 border-t bg-slate-50">
              <Button variant="outline" onClick={() => setShowInvoicesModal(false)} className="w-full">
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Carnês por Empresa */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Carnês por Empresa
          </CardTitle>
          <CardDescription>Baixe ou imprima carnês completos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(invoicesByCompany).map(([companyId, data]) => {
              const pendingCount = data.invoices.filter(i => i.status === "pending").length;
              const paidCount = data.invoices.filter(i => i.status === "paid").length;
              const today = new Date().toISOString().split('T')[0];
              const overdueCount = data.invoices.filter(i => 
                (i.status === "overdue") || (i.status === "pending" && i.due_date < today)
              ).length;
              const totalValue = data.invoices.reduce((sum, i) => sum + i.amount, 0);
              const pendingValue = data.invoices
                .filter(i => i.status === "pending" || i.status === "overdue")
                .reduce((sum, i) => sum + i.amount, 0);
              
              return (
                <Card 
                  key={companyId} 
                  className="border cursor-pointer hover:shadow-lg hover:border-sky-300 transition-all"
                  onClick={() => openCompanyBoletosModal(companyId, data)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-slate-900">{data.company_name}</p>
                        <p className="text-sm text-slate-500">{data.invoices.length} parcelas</p>
                      </div>
                      <span className="text-lg font-bold text-ciaponto-cyan">R$ {totalValue.toFixed(2)}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs mb-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{paidCount} pagas</span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">{pendingCount - overdueCount} pendentes</span>
                      {overdueCount > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{overdueCount} vencidas</span>
                      )}
                    </div>
                    {pendingValue > 0 && (
                      <p className="text-xs text-amber-600 font-medium mb-3">
                        Em aberto: R$ {pendingValue.toFixed(2)}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          generateCarnePDF(data.company_name, data.invoices);
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCompanyBoletosModal(companyId, data);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Boletos
                      </Button>
                      {pendingCount === 0 && overdueCount === 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          disabled={archivingCompany === companyId}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveCompany(companyId, data.company_name);
                          }}
                          title="Arquivar carnê (sem parcelas em aberto)"
                          data-testid={`archive-carnet-${companyId}`}
                        >
                          {archivingCompany === companyId ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></div>
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Card de Clientes Cancelados/Arquivados */}
      {archivedCompanies.length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2 text-slate-500">
                  <Archive className="w-5 h-5" />
                  Clientes Cancelados
                </CardTitle>
                <CardDescription>{archivedCompanies.length} empresa(s) com carnê arquivado</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchivedModal(!showArchivedModal)}
              >
                {showArchivedModal ? "Ocultar" : "Ver Detalhes"}
              </Button>
            </div>
          </CardHeader>
          {showArchivedModal && (
            <CardContent>
              <div className="space-y-3">
                {archivedCompanies.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{item.company_name}</p>
                        <p className="text-xs text-slate-500">
                          {item.paid_count} parcelas pagas | Receita: R$ {item.total_revenue?.toFixed(2)} | Arquivado em {new Date(item.archived_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[#1CA1BA] border-[#1CA1BA]/40 hover:bg-[#1CA1BA]/10"
                      onClick={() => handleRestoreCompany(item.company_id)}
                      data-testid={`restore-carnet-${item.company_id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restaurar
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Modal de Boletos por Empresa */}
      {showCompanyBoletosModal && selectedCompanyBoletos && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Boletos - {selectedCompanyBoletos.companyName}
                  </CardTitle>
                  <CardDescription className="text-sky-100">
                    Gerencie os boletos desta empresa
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowCompanyBoletosModal(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-0 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Filtros */}
              <div className="p-4 bg-slate-50 border-b flex flex-wrap gap-2 items-center justify-between">
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant={companyBoletosFilter === "all" ? "default" : "outline"}
                    onClick={() => setCompanyBoletosFilter("all")}
                  >
                    Todos ({selectedCompanyBoletos.invoices.length})
                  </Button>
                  <Button 
                    size="sm" 
                    variant={companyBoletosFilter === "pending" ? "default" : "outline"}
                    onClick={() => setCompanyBoletosFilter("pending")}
                    className={companyBoletosFilter === "pending" ? "bg-yellow-500" : ""}
                  >
                    Em Aberto
                  </Button>
                  <Button 
                    size="sm" 
                    variant={companyBoletosFilter === "overdue" ? "default" : "outline"}
                    onClick={() => setCompanyBoletosFilter("overdue")}
                    className={companyBoletosFilter === "overdue" ? "bg-red-500" : ""}
                  >
                    Vencidos
                  </Button>
                  <Button 
                    size="sm" 
                    variant={companyBoletosFilter === "paid" ? "default" : "outline"}
                    onClick={() => setCompanyBoletosFilter("paid")}
                    className={companyBoletosFilter === "paid" ? "bg-green-500" : ""}
                  >
                    Pagos
                  </Button>
                </div>
                
                {/* Ações em lote */}
                <div className="flex gap-2 items-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={selectAllVisibleBoletos}
                    disabled={getFilteredCompanyBoletos().filter(i => i.status !== "paid" && i.status !== "cancelled").length === 0}
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {selectedBoletos.length > 0 ? "Desmarcar" : "Selecionar"} Todos
                  </Button>
                  {selectedBoletos.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleCancelSelectedBoletos}
                      disabled={cancelingBoletos}
                    >
                      {cancelingBoletos ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      ) : (
                        <Trash2 className="w-4 h-4 mr-1" />
                      )}
                      Cancelar ({selectedBoletos.length})
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Tabela de boletos */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600 w-10">
                        <input
                          type="checkbox"
                          checked={selectedBoletos.length > 0 && 
                            selectedBoletos.length === getFilteredCompanyBoletos()
                              .filter(i => i.status !== "paid" && i.status !== "cancelled").length
                          }
                          onChange={selectAllVisibleBoletos}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </th>
                      <th className="text-left p-3 font-medium text-slate-600">Parcela</th>
                      <th className="text-left p-3 font-medium text-slate-600">Vencimento</th>
                      <th className="text-left p-3 font-medium text-slate-600">Valor</th>
                      <th className="text-left p-3 font-medium text-slate-600">Status</th>
                      <th className="text-right p-3 font-medium text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredCompanyBoletos().map((invoice) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isOverdue = invoice.status === "pending" && invoice.due_date < today;
                      const isPaid = invoice.status === "paid";
                      const isCancelled = invoice.status === "cancelled";
                      const canSelect = !isPaid && !isCancelled;
                      
                      return (
                        <tr 
                          key={invoice.id} 
                          className={`border-b border-slate-100 hover:bg-slate-50 ${
                            selectedBoletos.includes(invoice.id) ? 'bg-sky-50' : ''
                          }`}
                        >
                          <td className="p-3">
                            {canSelect && (
                              <input
                                type="checkbox"
                                checked={selectedBoletos.includes(invoice.id)}
                                onChange={() => toggleBoletoSelection(invoice.id)}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                            )}
                          </td>
                          <td className="p-3 font-medium">{invoice.invoice_number}/12</td>
                          <td className="p-3">
                            <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                              {new Date(invoice.due_date).toLocaleDateString("pt-BR")}
                            </span>
                          </td>
                          <td className="p-3 font-medium">R$ {invoice.amount?.toFixed(2)}</td>
                          <td className="p-3">
                            {isPaid ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                Pago
                              </span>
                            ) : isCancelled ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-500">
                                Cancelado
                              </span>
                            ) : isOverdue ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Vencido
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                                Pendente
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Botão Gerar Nota Fiscal (só para boletos PAGOS) */}
                              {isPaid && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEmitirNotaBoleto(invoice)}
                                  disabled={enviandoSpeedGov === invoice.id || invoice.has_nfse_oficial}
                                  className={`${invoice.has_nfse_oficial
                                    ? 'text-emerald-600 border-emerald-300 bg-emerald-50 cursor-default'
                                    : 'text-purple-600 border-purple-300 hover:bg-purple-50'} text-xs font-medium`}
                                  title={invoice.has_nfse_oficial
                                    ? `NFS-e #${invoice.numero_nfse_oficial} já emitida`
                                    : "Emitir NFS-e oficial na Prefeitura"}
                                  data-testid={`btn-gerar-nota-${invoice.id}`}
                                >
                                  {enviandoSpeedGov === invoice.id ? (
                                    <span className="flex items-center gap-1">
                                      <span className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
                                      Enviando...
                                    </span>
                                  ) : invoice.has_nfse_oficial ? (
                                    <span className="flex items-center gap-1">
                                      <CheckCircle className="w-3.5 h-3.5" />
                                      NFS-e #{invoice.numero_nfse_oficial}
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <FileText className="w-3.5 h-3.5" />
                                      Gerar Nota
                                    </span>
                                  )}
                                </Button>
                              )}
                              {/* Botão WhatsApp Cobrança */}
                              {!isPaid && !isCancelled && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (invoice.boleto_link) {
                                      sendWhatsAppCobranca(invoice, selectedCompanyBoletos, isOverdue);
                                    } else {
                                      toast.error("Gere o boleto no EFI primeiro para enviar cobrança");
                                    }
                                  }}
                                  className={`${isOverdue ? 'text-red-600 border-red-300 hover:bg-red-50' : 'text-green-600 border-green-300 hover:bg-green-50'} ${!invoice.boleto_link ? 'opacity-50' : ''}`}
                                  title={invoice.boleto_link ? (isOverdue ? "Enviar cobrança (vencido)" : "Enviar lembrete") : "Gere o boleto primeiro"}
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {invoice.boleto_link && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(invoice.boleto_link, "_blank")}
                                  className="text-ciaponto-cyan border-sky-300 hover:bg-cyan-50"
                                  title="Ver Boleto"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                              {canSelect && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleCancelSingleBoleto(invoice.id)}
                                  title="Cancelar boleto"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {getFilteredCompanyBoletos().length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhum boleto encontrado com este filtro</p>
                  </div>
                )}
              </div>
              
              {/* Resumo */}
              <div className="p-4 bg-slate-50 border-t">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Total de Boletos</p>
                    <p className="text-lg font-bold text-slate-700">{selectedCompanyBoletos.invoices.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Valor Total</p>
                    <p className="text-lg font-bold text-ciaponto-cyan">
                      R$ {selectedCompanyBoletos.invoices.reduce((sum, i) => sum + i.amount, 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Em Aberto</p>
                    <p className="text-lg font-bold text-amber-600">
                      R$ {selectedCompanyBoletos.invoices
                        .filter(i => i.status === "pending" || i.status === "overdue")
                        .reduce((sum, i) => sum + i.amount, 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Pagos</p>
                    <p className="text-lg font-bold text-green-600">
                      R$ {selectedCompanyBoletos.invoices
                        .filter(i => i.status === "paid")
                        .reduce((sum, i) => sum + i.amount, 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            
            <div className="p-4 border-t bg-white flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => generateCarnePDF(selectedCompanyBoletos.companyName, selectedCompanyBoletos.invoices)}
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar PDF Completo
              </Button>
              <Button variant="default" onClick={() => setShowCompanyBoletosModal(false)}>
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Lista de faturas */}
      <Card className="border border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading">Todas as Faturas</CardTitle>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant={filter === "all" ? "default" : "outline"}
                onClick={() => setFilter("all")}
              >
                Todas
              </Button>
              <Button 
                size="sm" 
                variant={filter === "pending" ? "default" : "outline"}
                onClick={() => setFilter("pending")}
                className={filter === "pending" ? "bg-yellow-500" : ""}
              >
                Pendentes
              </Button>
              <Button 
                size="sm" 
                variant={filter === "overdue" ? "default" : "outline"}
                onClick={() => setFilter("overdue")}
                className={filter === "overdue" ? "bg-red-500" : ""}
              >
                Em Atraso
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                  <th className="text-left p-3 font-medium text-slate-600">Parcela</th>
                  <th className="text-left p-3 font-medium text-slate-600">Vencimento</th>
                  <th className="text-left p-3 font-medium text-slate-600">Valor</th>
                  <th className="text-left p-3 font-medium text-slate-600">Status</th>
                  <th className="text-right p-3 font-medium text-slate-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.slice(0, 50).map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium">{invoice.company_name || "N/A"}</td>
                    <td className="p-3">{invoice.invoice_number}/12</td>
                    <td className="p-3">{new Date(invoice.due_date).toLocaleDateString("pt-BR")}</td>
                    <td className="p-3 font-medium">R$ {invoice.amount?.toFixed(2)}</td>
                    <td className="p-3">{getStatusBadge(invoice.status, invoice.due_date)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {invoice.boleto_link ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(invoice.boleto_link, "_blank")}
                            className="text-ciaponto-cyan border-sky-300 hover:bg-cyan-50"
                            title="Ver Boleto EFI"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Boleto
                          </Button>
                        ) : invoice.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateBoleto(invoice.id)}
                            disabled={generatingBoleto === invoice.id}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            title="Gerar boleto no EFI Bank"
                          >
                            {generatingBoleto === invoice.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500 mr-1"></div>
                            ) : (
                              <CreditCard className="w-4 h-4 mr-1" />
                            )}
                            Gerar Boleto
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => generateBoletoPDF(invoice, invoice.company_name || "Empresa")}
                          title="Baixar Recibo"
                        >
                          <FileDown className="w-4 h-4 text-slate-600" />
                        </Button>
                        {invoice.status !== "paid" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkPaid(invoice.id)}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Pago
                          </Button>
                        )}
                        {invoice.status !== "paid" && invoice.status !== "cancelled" && invoice.status !== "canceled" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            disabled={deletingInvoice === invoice.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Cancelar Fatura"
                          >
                            {deletingInvoice === invoice.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Documentos Page Component - Termos de Uso
function DocumentosPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${API}/superadmin/companies`);
        setCompanies(response.data || []);
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const openPdf = (tipo) => {
    const token = localStorage.getItem("token");
    let url = `${API}/termos/${tipo}/pdf?token=${token}`;
    if (selectedCompanyId) {
      url += `&company_id=${selectedCompanyId}`;
    }
    window.open(url, '_blank');
  };

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Documentos</h1>
        <p className="text-slate-600">Termos de uso e documentos legais da plataforma</p>
      </div>

      {/* Seletor de Empresa */}
      <Card className="border-[#1CA1BA]/30">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1CA1BA]" />
              <span className="font-medium text-slate-700">Selecione a Empresa:</span>
            </div>
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="flex-1 w-full sm:w-auto px-4 py-2 border border-[#1CA1BA]/40 rounded-lg bg-white text-slate-700 focus:ring-2 focus:ring-[#1CA1BA] focus:border-[#1CA1BA]"
              data-testid="select-company-docs"
            >
              <option value="">-- Selecione uma empresa --</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.trade_name || company.name}
                </option>
              ))}
            </select>
          </div>
          {selectedCompany && (
            <p className="text-sm text-[#17889E] mt-2">
              Os PDFs serão gerados com o nome: <strong>{selectedCompany.trade_name || selectedCompany.name}</strong>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1CA1BA]" />
            Termos de Uso
          </CardTitle>
          <CardDescription>
            {selectedCompanyId 
              ? `Gerar termos para: ${selectedCompany?.trade_name || selectedCompany?.name}`
              : "Selecione uma empresa acima para gerar os termos personalizados"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Termo Empresa */}
            <div className="border rounded-xl p-6 hover:shadow-md transition-shadow bg-gradient-to-br from-[#1CA1BA]/5 to-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-[#1CA1BA]/15 rounded-xl">
                  <Building2 className="w-6 h-6 text-[#1CA1BA]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Termo de Uso - Empresa</h3>
                  <p className="text-sm text-slate-500">Contrato de utilização da plataforma</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Documento contendo as condições de uso, responsabilidades, política de privacidade e LGPD para empresas contratantes.
              </p>
              <Button 
                className="w-full bg-[#1CA1BA] hover:bg-[#17889E]"
                disabled={!selectedCompanyId}
                onClick={() => openPdf("empresa")}
                data-testid="download-termo-empresa-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {selectedCompanyId ? "Abrir Termo da Empresa" : "Selecione uma empresa"}
              </Button>
            </div>

            {/* Termo Funcionário */}
            <div className="border rounded-xl p-6 hover:shadow-md transition-shadow bg-gradient-to-br from-emerald-50 to-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Termo de Uso - Funcionário</h3>
                  <p className="text-sm text-slate-500">Termo para assinatura dos colaboradores</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Documento com o nome da empresa e campos em branco para preenchimento dos dados do funcionário.
              </p>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={!selectedCompanyId}
                onClick={() => openPdf("funcionario")}
                data-testid="download-termo-funcionario-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {selectedCompanyId ? "Abrir Termo do Funcionário" : "Selecione uma empresa"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Backup Page Component
function BackupPage() {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/backup`);
      const backup = response.data;
      
      // Criar arquivo JSON para download
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_control-isp_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup criado com sucesso! ${backup.stats.companies} empresas, ${backup.stats.employees} funcionários, ${backup.stats.time_records} registros.`);
    } catch (error) {
      toast.error("Erro ao criar backup");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setRestoring(true);
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      if (!backupData.data || !backupData.backup_version) {
        throw new Error("Arquivo de backup inválido");
      }

      const response = await axios.post(`${API}/superadmin/restore`, backupData);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao restaurar backup. Verifique se o arquivo é válido.");
    } finally {
      setRestoring(false);
      e.target.value = "";
    }
  };

  const handleClearDatabase = async () => {
    if (clearConfirmText !== "CONFIRMAR_LIMPEZA") {
      toast.error("Digite 'CONFIRMAR_LIMPEZA' para prosseguir");
      return;
    }

    setClearing(true);
    try {
      const response = await axios.delete(`${API}/superadmin/clear-database?confirm=CONFIRMAR_LIMPEZA`);
      toast.success(response.data.message);
      setShowClearConfirm(false);
      setClearConfirmText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao limpar banco de dados");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Backup e Restauração</h1>
        <p className="text-slate-600">Gerencie os dados do sistema</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Backup Card */}
        <Card className="border border-slate-200">
          <CardHeader>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2">
              <Settings className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="font-heading text-lg">Fazer Backup</CardTitle>
            <CardDescription>
              Exporte todos os dados (empresas, funcionários, registros) para um arquivo JSON.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleBackup} 
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="backup-btn"
            >
              {loading ? "Gerando backup..." : "Baixar Backup"}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card className="border border-slate-200">
          <CardHeader>
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-2">
              <Settings className="w-6 h-6 text-ciaponto-cyan" />
            </div>
            <CardTitle className="font-heading text-lg">Restaurar Backup</CardTitle>
            <CardDescription>
              Importe um arquivo de backup para restaurar os dados do sistema.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              accept=".json"
              onChange={handleRestore}
              className="hidden"
              id="restore-input"
              disabled={restoring}
            />
            <label htmlFor="restore-input">
              <Button 
                asChild
                disabled={restoring}
                className="w-full bg-[#1CA1BA]-dark hover:bg-sky-700 cursor-pointer"
              >
                <span>{restoring ? "Restaurando..." : "Selecionar Arquivo"}</span>
              </Button>
            </label>
            <p className="text-xs text-slate-500 mt-2 text-center">Apenas arquivos .json</p>
          </CardContent>
        </Card>

        {/* Clear Database Card */}
        <Card className="border border-red-200 bg-red-50/50">
          <CardHeader>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle className="font-heading text-lg text-red-700">Limpar Banco de Dados</CardTitle>
            <CardDescription className="text-red-600">
              Remove TODOS os dados do sistema (exceto sua conta de admin). Esta ação é irreversível!
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showClearConfirm ? (
              <Button 
                onClick={() => setShowClearConfirm(true)}
                variant="destructive"
                className="w-full"
                data-testid="clear-db-btn"
              >
                Limpar Tudo
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-red-700 font-medium">
                  Digite &quot;CONFIRMAR_LIMPEZA&quot; para prosseguir:
                </p>
                <Input
                  value={clearConfirmText}
                  onChange={(e) => setClearConfirmText(e.target.value)}
                  placeholder="CONFIRMAR_LIMPEZA"
                  className="border-red-300"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowClearConfirm(false);
                      setClearConfirmText("");
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleClearDatabase}
                    disabled={clearing || clearConfirmText !== "CONFIRMAR_LIMPEZA"}
                    className="flex-1"
                  >
                    {clearing ? "Limpando..." : "Confirmar"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="border border-slate-200 bg-slate-50">
        <CardContent className="p-6">
          <h4 className="font-medium text-slate-900 mb-2">Informações sobre Backup</h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• O backup inclui: empresas, usuários, funcionários, registros de ponto e configurações.</li>
            <li>• Recomendamos fazer backup regularmente para evitar perda de dados.</li>
            <li>• Ao restaurar, os dados atuais serão substituídos pelos dados do backup.</li>
            <li>• A limpeza do banco remove todos os dados, exceto sua conta de Super Admin.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// Visitantes Page - Estatísticas de visitantes do site
function VisitantesPage() {
  const [stats, setStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchVisitorData();
  }, [page]);

  const fetchVisitorData = async () => {
    try {
      setLoading(true);
      
      // Buscar estatísticas gerais
      const statsRes = await axios.get(`${API}/visitors/stats`);
      setStats(statsRes.data);
      
      // Buscar detalhes dos visitantes
      const detailsRes = await axios.get(`${API}/visitors/details?page=${page}&limit=50`);
      setVisitors(detailsRes.data.visitors);
      setDailyStats(detailsRes.data.daily_stats);
      setTotal(detailsRes.data.total);
      
    } catch (error) {
      console.error("Erro ao buscar dados de visitantes:", error);
      toast.error("Erro ao carregar dados de visitantes");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-slate-900">Visitantes do Site</h1>
        <Button onClick={fetchVisitorData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-ciaponto-cyan" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.total?.toLocaleString('pt-BR') || 0}
                </p>
                <p className="text-sm text-slate-500">Total de Visitantes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.today?.toLocaleString('pt-BR') || 0}
                </p>
                <p className="text-sm text-slate-500">Visitantes Hoje</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.last_7_days?.toLocaleString('pt-BR') || 0}
                </p>
                <p className="text-sm text-slate-500">Últimos 7 Dias</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Visitantes por Dia */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Visitantes por Dia (Últimos 30 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyStats.length > 0 ? (
            <div className="space-y-2">
              {dailyStats.slice(0, 15).map((day) => (
                <div key={day._id} className="flex items-center gap-4">
                  <span className="text-sm text-slate-500 w-24">
                    {new Date(day._id + 'T00:00:00').toLocaleDateString('pt-BR', { 
                      day: '2-digit', 
                      month: 'short' 
                    })}
                  </span>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-[#1CA1BA] h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, (day.count / Math.max(...dailyStats.map(d => d.count))) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-700 w-12 text-right">
                    {day.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
          )}
        </CardContent>
      </Card>

      {/* Lista de Visitantes Recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Visitantes Recentes</CardTitle>
          <CardDescription>Total: {total} visitantes únicos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Data/Hora</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">IP</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Navegador/Dispositivo</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((visitor, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {new Date(visitor.timestamp).toLocaleString('pt-BR')}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700 font-mono">
                      {visitor.ip}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500 truncate max-w-xs" title={visitor.user_agent}>
                      {visitor.user_agent?.substring(0, 60)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {total > 50 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <span className="text-sm text-slate-500">
                Página {page} de {Math.ceil(total / 50)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / 50)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// NFS-e (Notas Fiscais de Serviço) Page
function NotasFiscaisPage() {
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [nfseList, setNfseList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const { user } = useAuth();
  
  // Estados para download de XML por mês
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [downloadingXML, setDownloadingXML] = useState(false);
  
  // Estados para relatório PDF
  const [relatorioDataInicio, setRelatorioDataInicio] = useState("");
  const [relatorioDataFim, setRelatorioDataFim] = useState("");
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);

  useEffect(() => {
    if (user?.role === "superadmin") {
      fetchData();
      // Definir datas padrão do mês atual
      const hoje = new Date();
      const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      setRelatorioDataInicio(primeiroDia.toISOString().split('T')[0]);
      setRelatorioDataFim(ultimoDia.toISOString().split('T')[0]);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, nfseRes] = await Promise.all([
        axios.get(`${API}/nfse/invoices-paid`),
        axios.get(`${API}/nfse`)
      ]);
      setPaidInvoices(invoicesRes.data);
      setNfseList(nfseRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados de notas fiscais");
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar relatório PDF
  const handleGerarRelatorioPDF = async () => {
    if (!relatorioDataInicio || !relatorioDataFim) {
      toast.error("Selecione o período do relatório");
      return;
    }

    try {
      setGerandoRelatorio(true);
      
      // Importar jsPDF e autoTable
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
      const autoTableModule = await import('jspdf-autotable');
      
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Dados da empresa (CIAPAY)
      const empresa = {
        nome: "CIAPAY SISTEM LTDA",
        endereco: "R. João da Mata Bandeira, 238 - Jardim Oásis - Iguatu - CE - 63507-425",
        cnpj: "64.199.817/0001-13",
        ie: "Isento",
        im: "6558006",
        fone: "(88) 99947-1978",
        email: "ciapontosystem@gmail.com"
      };
      
      // ========== CABEÇALHO COM LOGO ==========
      // Carregar logo da Ciapay
      try {
        const logoImg = new Image();
        logoImg.src = '/logo-ciapay.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(resolve, 2000); // Timeout de 2s
        });
        if (logoImg.complete && logoImg.naturalWidth > 0) {
          // Adicionar logo no canto superior esquerdo (aumentada 80%)
          // Original: 35x15mm -> Nova: 63x27mm
          doc.addImage(logoImg, 'PNG', 10, 5, 63, 27);
        }
      } catch (logoError) {
        console.log("Logo não carregada, continuando sem logo");
      }
      
      // Nome da empresa (título principal) - ajustado para não sobrepor a logo
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text(empresa.nome, pageWidth / 2 + 20, 18, { align: "center" });
      
      // Endereço
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(empresa.endereco, pageWidth / 2 + 20, 25, { align: "center" });
      
      // CNPJ, IE, IM, Fone
      doc.setFontSize(9);
      doc.text(`CNPJ: ${empresa.cnpj}   |   IE: ${empresa.ie}   |   IM: ${empresa.im}   |   Fone: ${empresa.fone}`, pageWidth / 2 + 20, 31, { align: "center" });
      
      // Email
      doc.text(`Email: ${empresa.email}`, pageWidth / 2 + 20, 37, { align: "center" });
      
      // Info do relatório (canto direito)
      doc.setFontSize(8);
      doc.text(`Usuário: ${user?.name || "Super Admin"}`, pageWidth - 15, 12, { align: "right" });
      doc.text(`Data: ${new Date().toLocaleString('pt-BR')}`, pageWidth - 15, 17, { align: "right" });
      
      // Linha separadora dupla (ajustada para logo maior)
      doc.setLineWidth(0.8);
      doc.line(15, 44, pageWidth - 15, 44);
      doc.setLineWidth(0.3);
      doc.line(15, 45.5, pageWidth - 15, 45.5);
      
      // Título do período
      const dataInicioFormatada = new Date(relatorioDataInicio + 'T00:00:00').toLocaleDateString('pt-BR');
      const dataFimFormatada = new Date(relatorioDataFim + 'T00:00:00').toLocaleDateString('pt-BR');
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`Relatório de Notas Fiscais Emitidas - Período: ${dataInicioFormatada} a ${dataFimFormatada}`, pageWidth / 2, 54, { align: "center" });
      
      // ========== FILTRAR NOTAS ==========
      const dataInicio = new Date(relatorioDataInicio + 'T00:00:00');
      const dataFim = new Date(relatorioDataFim + 'T23:59:59');
      
      // FILTRAR APENAS NOTAS EMITIDAS (excluir canceladas)
      const notasFiltradas = nfseList.filter(nf => {
        const dataNota = new Date(nf.created_at);
        const dentroPerido = dataNota >= dataInicio && dataNota <= dataFim;
        const naoECancelada = nf.status !== "cancelada";
        return dentroPerido && naoECancelada;
      });
      
      if (notasFiltradas.length === 0) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text("Nenhuma nota fiscal emitida encontrada no período selecionado.", pageWidth / 2, 74, { align: "center" });
      } else {
        // Preparar dados da tabela - incluindo IE e IM do tomador
        const tableData = notasFiltradas.map(nf => [
          nf.numero_nfse || "-",
          "Emitida",
          new Date(nf.created_at || nf.data_emissao).toLocaleDateString('pt-BR'),
          nf.tomador_razao_social || nf.company_name || "-",
          nf.tomador_cnpj || "-",
          nf.tomador_inscricao_estadual || "N/I",
          nf.tomador_inscricao_municipal || "N/I",
          `R$ ${(nf.valor_servico || nf.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        ]);
        
        // Usar autoTable - verificar qual método está disponível
        const autoTableFn = autoTableModule.default || autoTableModule;
        if (typeof autoTableFn === 'function') {
          autoTableFn(doc, {
            startY: 62,  // Ajustado para logo maior
            head: [['NF', 'Status', 'Data Emissão', 'Cliente', 'CNPJ/CPF', 'IE', 'IM', 'Valor']],
            body: tableData,
            theme: 'grid',
            headStyles: {
              fillColor: [51, 51, 51],
              textColor: [255, 255, 255],
              fontSize: 8,
              fontStyle: 'bold',
              halign: 'center'
            },
            bodyStyles: {
              fontSize: 8,
              halign: 'center'
            },
            columnStyles: {
              0: { cellWidth: 15 },
              1: { cellWidth: 20 },
              2: { cellWidth: 25 },
              3: { cellWidth: 60, halign: 'left' },
              4: { cellWidth: 35 },
              5: { cellWidth: 30 },
              6: { cellWidth: 25 },
              7: { cellWidth: 30, halign: 'right' }
            },
            margin: { left: 15, right: 15 }
          });
        } else if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            startY: 62,  // Ajustado para logo maior
            head: [['NF', 'Status', 'Data Emissão', 'Cliente', 'CNPJ/CPF', 'IE', 'IM', 'Valor']],
            body: tableData,
            theme: 'grid',
            headStyles: {
              fillColor: [51, 51, 51],
              textColor: [255, 255, 255],
              fontSize: 8,
              fontStyle: 'bold',
              halign: 'center'
            },
            bodyStyles: {
              fontSize: 8,
              halign: 'center'
            },
            columnStyles: {
              0: { cellWidth: 15 },
              1: { cellWidth: 20 },
              2: { cellWidth: 25 },
              3: { cellWidth: 60, halign: 'left' },
              4: { cellWidth: 35 },
              5: { cellWidth: 30 },
              6: { cellWidth: 25 },
              7: { cellWidth: 30, halign: 'right' }
            },
            margin: { left: 15, right: 15 }
          });
        } else {
          // Fallback: criar tabela manualmente sem autoTable
          let y = 66;  // Ajustado para logo maior
          const headers = ['NF', 'Status', 'Tipo', 'Data Emissão', 'Cliente', 'Valor'];
          const colWidths = [20, 25, 20, 30, 100, 30];
          let x = 15;
          
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          headers.forEach((header, i) => {
            doc.text(header, x, y);
            x += colWidths[i];
          });
          
          y += 7;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          
          tableData.forEach(row => {
            x = 15;
            doc.text(String(row[0]), x, y);
            doc.text(String(row[1]), x + colWidths[0], y);
            doc.text(String(row[2]), x + colWidths[0] + colWidths[1], y);
            doc.text(String(row[3]), x + colWidths[0] + colWidths[1] + colWidths[2], y);
            doc.text(String(row[7]).substring(0, 40), x + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], y);
            doc.text(String(row[8]), x + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4], y);
            y += 6;
            if (y > 180) {
              doc.addPage();
              y = 20;
            }
          });
        }
        
        // Totalizadores - usar valor_servico corretamente
        const totalValor = notasFiltradas.reduce((acc, nf) => acc + (nf.valor_servico || nf.valor || 0), 0);
        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 180;
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Total de Notas Emitidas: ${notasFiltradas.length}`, 15, finalY);
        doc.text(`Valor Total: R$ ${totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, pageWidth - 15, finalY, { align: "right" });
      }
      
      // Rodapé
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`Página ${i} de ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text("CiaPonto - Sistema de Ponto Eletrônico", pageWidth / 2, doc.internal.pageSize.getHeight() - 5, { align: "center" });
      }
      
      // Download
      doc.save(`Relatorio_NFS-e_${relatorioDataInicio}_${relatorioDataFim}.pdf`);
      toast.success("Relatório gerado com sucesso!");
      
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      toast.error("Erro ao gerar relatório PDF: " + error.message);
    } finally {
      setGerandoRelatorio(false);
    }
  };

  const handleGenerateNFSe = async (invoiceId) => {
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/nfse/generate`, { invoice_id: invoiceId });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Erro ao gerar NFS-e:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar nota fiscal");
    } finally {
      setGenerating(false);
    }
  };

  const [enviandoSpeedGov, setEnviandoSpeedGov] = useState(null);

  const handleEnviarSpeedGov = async (nfse) => {
    const invoiceId = nfse.invoice_id;
    if (!invoiceId) {
      toast.error("Fatura não vinculada a esta nota fiscal");
      return;
    }

    const confirmado = window.confirm(
      `Enviar NFS-e #${nfse.numero_nfse} (${nfse.tomador_razao_social} - ${formatCurrency(nfse.valor_servico)}) ao SpeedGov da Prefeitura?\n\nAmbiente: PRODUÇÃO`
    );
    if (!confirmado) return;

    try {
      setEnviandoSpeedGov(nfse.id);
      toast.info("Enviando ao SpeedGov... aguarde");

      const response = await axios.post(`${API}/nfse/emitir-oficial`, {
        invoice_id: invoiceId,
        ambiente: "producao"
      });

      if (response.data.success) {
        const proto = response.data.protocolo;
        const nfseNum = response.data.numero_nfse;
        if (nfseNum) {
          toast.success(`NFS-e #${nfseNum} emitida com sucesso!`);
        } else if (proto) {
          toast.success(`Lote enviado ao SpeedGov! Protocolo: ${proto}. A nota será processada em alguns minutos.`, { duration: 8000 });
        } else {
          toast.success("Enviado ao SpeedGov com sucesso!");
        }
        fetchData();
      } else {
        toast.error(response.data.detail || "Erro no envio ao SpeedGov");
      }
    } catch (error) {
      console.error("Erro ao enviar ao SpeedGov:", error);
      const detail = error.response?.data?.detail || "Erro ao comunicar com SpeedGov";
      toast.error(detail);
    } finally {
      setEnviandoSpeedGov(null);
    }
  };

  const handleDownloadPDF = async (nfseId) => {
    try {
      const response = await axios.get(`${API}/nfse/${nfseId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe_${nfseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };
  
  // Download XML unificado por mês
  const handleDownloadXMLByMonth = async () => {
    try {
      setDownloadingXML(true);
      const response = await axios.get(`${API}/nfse/xml-mensal`, {
        params: { month: selectedMonth, year: selectedYear },
        responseType: 'blob'
      });
      
      // Verificar se retornou dados
      if (response.data.size === 0) {
        toast.error("Nenhuma nota fiscal encontrada para este período");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('pt-BR', { month: 'long' });
      link.setAttribute('download', `DPS_Lote_${monthName}_${selectedYear}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("XMLs das DPS baixados com sucesso!");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Nenhuma nota fiscal encontrada para este período");
      } else {
        toast.error("Erro ao baixar XML mensal");
      }
    } finally {
      setDownloadingXML(false);
    }
  };
  
  // Contar notas do mês selecionado
  const notasDoMes = nfseList.filter(nfse => {
    const dataEmissao = new Date(nfse.data_emissao);
    return dataEmissao.getMonth() + 1 === selectedMonth && 
           dataEmissao.getFullYear() === selectedYear &&
           nfse.status !== "cancelada";
  });

  const handleDownloadXML = async (nfseId) => {
    try {
      const response = await axios.get(`${API}/nfse/${nfseId}/xml`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe_${nfseId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("XML baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar XML");
    }
  };

  const handleCancelNFSe = async (nfseId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta nota fiscal?")) return;
    
    try {
      await axios.delete(`${API}/nfse/${nfseId}`);
      toast.success("Nota fiscal cancelada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao cancelar nota fiscal");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value || 0);
  };

  const pendingInvoices = paidInvoices.filter(inv => !inv.has_nfse);

  if (user?.role !== "superadmin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <p className="text-slate-600">Acesso restrito ao administrador master.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nfse-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Notas Fiscais de Serviço</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie as NFS-e das faturas pagas</p>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Pendentes</p>
                <p className="text-2xl font-bold text-amber-700">{pendingInvoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">Emitidas</p>
                <p className="text-2xl font-bold text-green-700">{nfseList.filter(n => n.status === "emitida").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <XCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600">Canceladas</p>
                <p className="text-2xl font-bold text-red-700">{nfseList.filter(n => n.status === "cancelada").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#1CA1BA]/10 to-[#1CA1BA]/20 border-[#1CA1BA]/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1CA1BA] rounded-lg">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-[#17889E]">Total Emitido</p>
                <p className="text-2xl font-bold text-[#1CA1BA]">
                  {formatCurrency(nfseList.filter(n => n.status === "emitida").reduce((sum, n) => sum + n.valor_servico, 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Card de Download XML Mensal */}
      <Card className="bg-gradient-to-br from-[#1CA1BA]/5 to-[#17889E]/10 border-[#1CA1BA]/30">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#1CA1BA] rounded-lg">
                <FileDown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#17889E]">Download DPS Mensal</h3>
                <p className="text-sm text-[#1CA1BA]">Baixe todas as DPS do mês em arquivos XML individuais (ZIP)</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex gap-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-3 py-2 border border-[#1CA1BA]/40 rounded-lg bg-white text-slate-700 focus:ring-2 focus:ring-[#1CA1BA] focus:border-[#1CA1BA]"
                >
                  <option value={1}>Janeiro</option>
                  <option value={2}>Fevereiro</option>
                  <option value={3}>Março</option>
                  <option value={4}>Abril</option>
                  <option value={5}>Maio</option>
                  <option value={6}>Junho</option>
                  <option value={7}>Julho</option>
                  <option value={8}>Agosto</option>
                  <option value={9}>Setembro</option>
                  <option value={10}>Outubro</option>
                  <option value={11}>Novembro</option>
                  <option value={12}>Dezembro</option>
                </select>
                
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-[#1CA1BA]/40 rounded-lg bg-white text-slate-700 focus:ring-2 focus:ring-[#1CA1BA] focus:border-[#1CA1BA]"
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#1CA1BA] font-medium">
                  {notasDoMes.length} nota(s)
                </span>
                <Button
                  onClick={handleDownloadXMLByMonth}
                  disabled={downloadingXML || notasDoMes.length === 0}
                  className="bg-[#1CA1BA] hover:bg-[#17889E] text-white"
                >
                  {downloadingXML ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Baixando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Baixar XML
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Relatório PDF de Notas Fiscais */}
      <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Relatório de Notas Fiscais (PDF)</h3>
                <p className="text-sm text-purple-600">Gere um relatório em PDF com o resumo das notas emitidas</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-purple-700 font-medium">Data Início</label>
                <Input
                  type="date"
                  value={relatorioDataInicio}
                  onChange={(e) => setRelatorioDataInicio(e.target.value)}
                  className="w-40 bg-white border-purple-200"
                />
              </div>
              <div>
                <label className="text-xs text-purple-700 font-medium">Data Fim</label>
                <Input
                  type="date"
                  value={relatorioDataFim}
                  onChange={(e) => setRelatorioDataFim(e.target.value)}
                  className="w-40 bg-white border-purple-200"
                />
              </div>
              <Button
                onClick={handleGerarRelatorioPDF}
                disabled={gerandoRelatorio}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {gerandoRelatorio ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Gerando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Gerar Relatório PDF
                  </>
                )}
              </Button>
            </div>
            
            <div className="text-xs text-purple-600 bg-purple-100 p-2 rounded">
              O relatório inclui: NF, Status, Data Emissão, Cliente, Valor e totalizadores.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Gerar XML para SpeedGov */}
      <Card className="bg-gradient-to-br from-emerald-50 to-cyan-50 border-emerald-200">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-500 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900">Gerar RPS para SpeedGov</h3>
                <p className="text-sm text-emerald-600">Gere os RPS (formato ABRASF) das faturas pagas para envio ao portal SpeedGov</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/nfse/gerar-xml-lote`);
                    if (response.data.gerados > 0) {
                      toast.success(`${response.data.gerados} XML(s) gerado(s) com sucesso!`);
                    } else {
                      toast.info(response.data.message);
                    }
                    fetchData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Erro ao gerar XMLs");
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar RPS de Todas as Faturas
              </Button>
              
              <Button
                onClick={async () => {
                  try {
                    window.open(`${API}/nfse/download-todos-xml`, '_blank');
                    toast.success("Download iniciado!");
                  } catch (error) {
                    toast.error("Erro ao baixar XMLs");
                  }
                }}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Todos os XMLs (ZIP)
              </Button>
              
              <Button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/nfse/xml-list`);
                    if (response.data.length === 0) {
                      toast.info("Nenhum XML gerado ainda");
                    } else {
                      toast.success(`${response.data.length} XML(s) disponíveis para download`);
                    }
                  } catch (error) {
                    toast.error("Erro ao verificar XMLs");
                  }
                }}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver XMLs Gerados
              </Button>
            </div>
            
            <div className="text-xs text-emerald-600 bg-emerald-100 p-3 rounded-lg">
              <p className="font-medium mb-2">Duas formas de enviar:</p>
              
              <div className="mb-3 p-2 bg-white/60 rounded-lg">
                <p className="font-bold text-emerald-800 mb-1">Opção 1 - Envio Manual (Portal SpeedGov):</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Clique em <strong>"Gerar RPS de Todas as Faturas"</strong></li>
                  <li>Clique em <strong>"Baixar Todos os XMLs"</strong> (baixa ZIP)</li>
                  <li>Descompacte o ZIP e extraia os arquivos XML</li>
                  <li>Acesse o portal SpeedGov, faça login com certificado digital</li>
                  <li>Importe/envie os XMLs no portal</li>
                  <li>Volte aqui e marque como emitida</li>
                </ol>
                <a
                  href="https://iss.speedgov.com.br/iguatu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-medium"
                >
                  <ExternalLink className="w-3 h-3" />
                  Abrir Portal SpeedGov Iguatu
                </a>
              </div>
              
              <div className="p-2 bg-white/60 rounded-lg">
                <p className="font-bold text-emerald-800 mb-1">Opção 2 - Envio Automático (WebService):</p>
                <p className="mb-1">O sistema envia direto ao SpeedGov usando o certificado digital instalado no servidor.</p>
                <p className="text-amber-600 font-medium">Clique em "Gerar NFS-e" em cada fatura na aba abaixo para enviar automaticamente.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "pending"
              ? "border-[#1CA1BA] text-ciaponto-cyan"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Faturas Pagas ({pendingInvoices.length} pendentes)
        </button>
        <button
          onClick={() => setActiveTab("generated")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "generated"
              ? "border-[#1CA1BA] text-ciaponto-cyan"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Notas Emitidas ({nfseList.length})
        </button>
      </div>

      {/* Tab: Faturas Pagas Pendentes */}
      {activeTab === "pending" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Faturas Pagas - Aguardando Emissão de NFS-e</CardTitle>
            <CardDescription>Clique em &quot;Gerar NFS-e&quot; para emitir a nota fiscal</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingInvoices.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>Todas as faturas pagas já têm nota fiscal gerada!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Fatura</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Empresa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">CNPJ</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Valor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Pago em</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium">#{invoice.invoice_number}</td>
                        <td className="py-3 px-4 text-sm">{invoice.company_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{invoice.company_cnpj}</td>
                        <td className="py-3 px-4 text-sm font-medium text-green-600">{formatCurrency(invoice.amount)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatDate(invoice.paid_at)}</td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            onClick={() => handleGenerateNFSe(invoice.id)}
                            disabled={generating}
                            className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                          >
                            {generating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            ) : (
                              <>
                                <Receipt className="w-4 h-4 mr-1" />
                                Gerar NFS-e
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Notas Emitidas */}
      {activeTab === "generated" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notas Fiscais Emitidas</CardTitle>
            <CardDescription>Baixe o PDF e XML para enviar ao contador</CardDescription>
          </CardHeader>
          <CardContent>
            {nfseList.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Nenhuma nota fiscal emitida ainda</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nº NFS-e</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tomador</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">CNPJ</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Valor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">ISS</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Emissão</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfseList.map((nfse) => (
                      <tr key={nfse.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-bold text-ciaponto-cyan">#{nfse.numero_nfse}</td>
                        <td className="py-3 px-4 text-sm">{nfse.tomador_razao_social}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{nfse.tomador_cnpj}</td>
                        <td className="py-3 px-4 text-sm font-medium">{formatCurrency(nfse.valor_servico)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatCurrency(nfse.valor_iss)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatDate(nfse.data_emissao)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            nfse.status === "emitida" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}>
                            {nfse.status === "emitida" ? "Emitida" : "Cancelada"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            {nfse.status === "emitida" && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleEnviarSpeedGov(nfse)}
                                disabled={enviandoSpeedGov === nfse.id}
                                title="Enviar ao SpeedGov (Prefeitura)"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1"
                                data-testid={`enviar-speedgov-${nfse.numero_nfse}`}
                              >
                                <Send className="w-3 h-3" />
                                {enviandoSpeedGov === nfse.id ? "Enviando..." : "SpeedGov"}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(nfse.id)}
                              title="Baixar PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadXML(nfse.id)}
                              title="Baixar XML"
                              className="text-orange-600"
                            >
                              <FileDown className="w-4 h-4" />
                            </Button>
                            {nfse.status === "emitida" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelNFSe(nfse.id)}
                                title="Cancelar NFS-e"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ==================== CiaPonto Indica+ - Gerenciamento de Afiliados ====================
function AfiliadosPage() {
  const [affiliates, setAffiliates] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [affiliateDetail, setAffiliateDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newAffiliatePassword, setNewAffiliatePassword] = useState("");
  const [showNewPasswordPlain, setShowNewPasswordPlain] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(null);
  // Edit affiliate modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Busca de afiliado
  const [affiliateSearch, setAffiliateSearch] = useState("");
  const [newAffiliate, setNewAffiliate] = useState({
    name: "", email: "", cpf_cnpj: "", phone: "", pix: "",
    login: "", password: "", payment_day: 10
  });

  const fetchAffiliates = async () => {
    try {
      const [affRes, compRes] = await Promise.all([
        axios.get(`${API}/affiliates`),
        axios.get(`${API}/affiliates/recent-companies`)
      ]);
      setAffiliates(affRes.data.affiliates);
      setRecentCompanies(compRes.data.companies);
    } catch (err) {
      toast.error("Erro ao carregar afiliados");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAffiliates(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newAffiliate.name || !newAffiliate.login || !newAffiliate.password) {
      toast.error("Preencha os campos obrigatorios"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/affiliates`, newAffiliate);
      toast.success("Afiliado criado com sucesso!");
      setShowCreateModal(false);
      setNewAffiliate({ name: "", email: "", cpf_cnpj: "", phone: "", pix: "", login: "", password: "", payment_day: 10 });
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar afiliado");
    } finally { setSubmitting(false); }
  };

  const handleViewDetail = async (affiliate) => {
    try {
      const res = await axios.get(`${API}/affiliates/${affiliate.id}`);
      setAffiliateDetail(res.data);
      setShowDetailView(true);
    } catch (err) { toast.error("Erro ao carregar detalhes"); }
  };

  const handleToggleActive = async (affiliate) => {
    try {
      if (affiliate.active) {
        await axios.delete(`${API}/affiliates/${affiliate.id}`);
        toast.success("Afiliado desativado");
      } else {
        await axios.put(`${API}/affiliates/${affiliate.id}`, { active: true });
        toast.success("Afiliado reativado");
      }
      fetchAffiliates();
    } catch (err) { toast.error("Erro ao atualizar status"); }
  };

  // Abrir modal para redefinir senha do afiliado
  const handleOpenResetPassword = (affiliate) => {
    setResetPasswordTarget(affiliate);
    setNewAffiliatePassword("");
    setShowNewPasswordPlain(false);
    setResetPasswordSuccess(null);
    setShowResetPasswordModal(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPasswordTarget) return;
    if (!newAffiliatePassword || newAffiliatePassword.length < 4) {
      toast.error("Senha deve ter no mínimo 4 caracteres");
      return;
    }
    try {
      setResettingPassword(true);
      const res = await axios.put(`${API}/affiliates/${resetPasswordTarget.id}/reset-password`, {
        new_password: newAffiliatePassword
      });
      // Mostrar sucesso COM as credenciais para o admin copiar e enviar
      setResetPasswordSuccess({
        login: res.data.login || resetPasswordTarget.login,
        password: newAffiliatePassword,
        name: res.data.name || resetPasswordTarget.name
      });
      toast.success(`Senha redefinida para "${res.data.login || resetPasswordTarget.login}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCloseResetPassword = () => {
    setShowResetPasswordModal(false);
    setNewAffiliatePassword("");
    setResetPasswordTarget(null);
    setResetPasswordSuccess(null);
  };

  // Sugerir senha aleatória forte
  const generateRandomPassword = () => {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars[Math.floor(Math.random() * chars.length)];
    }
    setNewAffiliatePassword(pwd);
    setShowNewPasswordPlain(true);
  };

  // Abrir modal de edição completa do afiliado
  const handleOpenEdit = (affiliate) => {
    setEditingAffiliate({
      id: affiliate.id,
      name: (affiliate.name || "").trim(),
      email: (affiliate.email || "").trim(),
      login: (affiliate.login || "").trim(),
      cpf_cnpj: (affiliate.cpf_cnpj || "").trim(),
      phone: (affiliate.phone || "").trim(),
      pix: (affiliate.pix || "").trim(),
      payment_day: affiliate.payment_day || 10,
      _original_login: affiliate.login,
      _original_email: affiliate.email
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingAffiliate) return;
    if (!editingAffiliate.name.trim() || !editingAffiliate.login.trim()) {
      toast.error("Nome e login são obrigatórios");
      return;
    }
    try {
      setSavingEdit(true);
      const payload = {
        name: editingAffiliate.name.trim(),
        email: editingAffiliate.email.trim(),
        login: editingAffiliate.login.trim(),
        cpf_cnpj: editingAffiliate.cpf_cnpj.trim(),
        phone: editingAffiliate.phone.trim(),
        pix: editingAffiliate.pix.trim(),
        payment_day: parseInt(editingAffiliate.payment_day) || 10
      };
      await axios.put(`${API}/affiliates/${editingAffiliate.id}`, payload);
      toast.success("Dados do afiliado atualizados com sucesso");
      setShowEditModal(false);
      setEditingAffiliate(null);
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar afiliado");
    } finally {
      setSavingEdit(false);
    }
  };

  // Excluir afiliado permanentemente (com confirmação simples)
  const handleDeletePermanent = async (affiliate) => {
    const confirmed = window.confirm(
      `⚠️ EXCLUIR PERMANENTEMENTE\n\n` +
      `Afiliado: ${affiliate.name}\n` +
      `Login: ${affiliate.login}\n\n` +
      `Esta ação NÃO pode ser desfeita.\n` +
      `Todas as indicações deste afiliado também serão excluídas.\n\n` +
      `Deseja continuar?`
    );
    if (!confirmed) return;
    try {
      const res = await axios.delete(`${API}/affiliates/${affiliate.id}/permanent`);
      const refsDeleted = res.data.referrals_deleted || 0;
      toast.success(
        `Afiliado "${affiliate.name}" excluído permanentemente${refsDeleted > 0 ? ` (${refsDeleted} indicação${refsDeleted > 1 ? 'ões' : ''} também removida${refsDeleted > 1 ? 's' : ''})` : ''}`,
        { duration: 6000 }
      );
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir afiliado");
    }
  };

  const handleUpdateReferralStatus = async (referralId, status) => {
    try {
      await axios.put(`${API}/affiliates/referrals/${referralId}/status`, { status });
      toast.success(`Status atualizado para ${status}`);
      if (affiliateDetail) {
        const res = await axios.get(`${API}/affiliates/${affiliateDetail.id}`);
        setAffiliateDetail(res.data);
      }
      fetchAffiliates();
    } catch (err) { toast.error("Erro ao atualizar status"); }
  };

  const handleUpdateCompanyStatus = async (companyId, status) => {
    try {
      await axios.put(`${API}/affiliates/companies/${companyId}/status`, { status });
      toast.success(`Empresa ${status === "inactive" ? "desativada" : "ativada"} com sucesso`);
      if (affiliateDetail) {
        const res = await axios.get(`${API}/affiliates/${affiliateDetail.id}`);
        setAffiliateDetail(res.data);
      }
      fetchAffiliates();
    } catch (err) { toast.error("Erro ao atualizar empresa"); }
  };

  const affiliateLoginUrl = `${window.location.origin}/indica/login`;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1CA1BA]"></div></div>;
  }

  // ======= DETAIL VIEW =======
  if (showDetailView && affiliateDetail) {
    const activeRefs = affiliateDetail.referrals?.filter(r => r.status === "ativo") || [];
    const pendingRefs = affiliateDetail.referrals?.filter(r => r.status === "pendente") || [];
    const cancelledRefs = affiliateDetail.referrals?.filter(r => r.status === "cancelado") || [];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setShowDetailView(false)} data-testid="back-to-list-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
            <div>
              <h1 className="font-heading font-bold text-2xl text-slate-900">{affiliateDetail.name}</h1>
              <p className="text-slate-500 text-sm">Detalhes do afiliado</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${affiliateDetail.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {affiliateDetail.active ? "Ativo" : "Inativo"}
          </span>
        </div>

        {/* Dados do Afiliado */}
        <Card>
          <CardHeader><CardTitle className="text-base">Dados do Afiliado</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-slate-500 block">Email</span><span className="font-medium">{affiliateDetail.email}</span></div>
              <div><span className="text-slate-500 block">CPF/CNPJ</span><span className="font-medium">{affiliateDetail.cpf_cnpj}</span></div>
              <div><span className="text-slate-500 block">Telefone</span><span className="font-medium">{affiliateDetail.phone}</span></div>
              <div><span className="text-slate-500 block">PIX</span><span className="font-medium">{affiliateDetail.pix}</span></div>
              <div><span className="text-slate-500 block">Login</span><span className="font-medium font-mono">{affiliateDetail.login}</span></div>
              <div><span className="text-slate-500 block">Dia Pagamento</span><span className="font-medium">Dia {affiliateDetail.payment_day}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-5 text-center">
              <p className="text-2xl font-bold text-blue-600">{affiliateDetail.total_referrals || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Total Indicacoes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <p className="text-2xl font-bold text-green-600">{affiliateDetail.active_referrals || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Ativos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <p className="text-2xl font-bold text-green-600">{affiliateDetail.empresas_em_dia || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Em Dia</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <p className="text-2xl font-bold text-red-600">{affiliateDetail.empresas_devendo || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Devendo</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-5 text-center">
              <p className="text-2xl font-bold text-purple-700">R$ {(affiliateDetail.valor_pagar_mensal || 0).toFixed(2)}</p>
              <p className="text-xs text-purple-600 mt-1">Pagar este Mes</p>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Empresas Cadastradas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Empresas Cadastradas ({affiliateDetail.referrals?.length || 0})</CardTitle>
            <CardDescription>Empresas indicadas por este afiliado com status de pagamento e cadastro</CardDescription>
          </CardHeader>
          <CardContent>
            {(!affiliateDetail.referrals || affiliateDetail.referrals.length === 0) ? (
              <p className="text-slate-500 text-sm text-center py-8">Nenhuma empresa cadastrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="affiliate-detail-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Empresa</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Responsavel / Email</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">CNPJ</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Plano</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Comissao/mes</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Pagamento</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Cadastro</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliateDetail.referrals.map((ref) => (
                      <tr key={ref.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-3">
                          <p className="font-medium text-slate-900">{ref.company_name}</p>
                          <p className="text-xs text-slate-400">{ref.num_employees} func.</p>
                        </td>
                        <td className="py-3 px-3">
                          <p className="text-slate-800">{ref.responsible_name}</p>
                          <p className="text-xs text-slate-500">{ref.empresa_email || ref.email}</p>
                        </td>
                        <td className="py-3 px-3 text-slate-600 text-xs font-mono">{ref.empresa_cnpj || ref.cnpj}</td>
                        <td className="py-3 px-3">
                          <span className="capitalize font-medium">{ref.plan}</span>
                          <p className="text-xs text-slate-500">R$ {ref.plan_price?.toFixed(2)}</p>
                        </td>
                        <td className="py-3 px-3 font-medium text-purple-600">R$ {ref.commission_recurring?.toFixed(2)}</td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
                            ref.empresa_status === "paid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {ref.empresa_status === "paid" ? "Em dia" : "Devendo"}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            ref.empresa_cadastro_status === "active" ? "bg-green-100 text-green-700" :
                            ref.empresa_cadastro_status === "inactive" ? "bg-red-100 text-red-700" :
                            "bg-yellow-100 text-yellow-700"
                          }`}>
                            {ref.empresa_cadastro_status === "active" ? "Completo" :
                             ref.empresa_cadastro_status === "inactive" ? "Desativado" : "Pendente"}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <select
                            value={ref.status}
                            onChange={(e) => handleUpdateReferralStatus(ref.id, e.target.value)}
                            className="text-xs border rounded p-1 mr-1"
                          >
                            <option value="pendente">Pendente</option>
                            <option value="ativo">Ativo</option>
                            <option value="cancelado">Cancelado</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ======= LIST VIEW =======
  const totalRecurring = affiliates.reduce((s, a) => s + (a.monthly_recurring || 0), 0);
  const totalActive = affiliates.reduce((s, a) => s + (a.active_referrals || 0), 0);
  const totalReferrals = affiliates.reduce((s, a) => s + (a.total_referrals || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-2">
            <Handshake className="w-6 h-6 text-[#1CA1BA]" /> CiaPonto Indica+
          </h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie o programa de afiliados</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#1CA1BA] hover:bg-[#17889E]" data-testid="create-affiliate-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Afiliado
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><p className="text-2xl font-bold">{affiliates.length}</p><p className="text-sm text-slate-500">Total Afiliados</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-2xl font-bold">{totalReferrals}</p><p className="text-sm text-slate-500">Total Indicacoes</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-2xl font-bold text-green-600">{totalActive}</p><p className="text-sm text-slate-500">Indicacoes Ativas</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-2xl font-bold text-purple-600">R$ {totalRecurring.toFixed(2)}</p><p className="text-sm text-slate-500">Comissoes Mensais</p></CardContent></Card>
      </div>

      {/* Link */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-slate-600 font-medium">Link de acesso para afiliados:</span>
            <code className="bg-slate-100 px-3 py-1.5 rounded text-sm text-slate-800 font-mono" data-testid="affiliate-access-url">{affiliateLoginUrl}</code>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(affiliateLoginUrl); toast.success("Link copiado!"); }}>Copiar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Ultimas Empresas Indicadas */}
      {recentCompanies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1CA1BA]" /> Ultimas Empresas Indicadas
            </CardTitle>
            <CardDescription>Empresas cadastradas recentemente por afiliados - complete as informacoes faltantes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-companies-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Empresa</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Afiliado</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Responsavel</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">CNPJ</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Plano</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Cadastro</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCompanies.slice(0, 10).map((comp) => (
                    <tr key={comp.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-900">{comp.name}</p>
                        <p className="text-xs text-slate-500">{comp.email}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-[#1CA1BA] font-medium text-xs">{comp.affiliate_name}</span>
                      </td>
                      <td className="py-3 px-2 text-slate-600">{comp.responsible_name}</td>
                      <td className="py-3 px-2 text-xs font-mono text-slate-600">{comp.cnpj}</td>
                      <td className="py-3 px-2 capitalize font-medium">{comp.plan}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          comp.status === "active" ? "bg-green-100 text-green-700" :
                          comp.status === "inactive" ? "bg-red-100 text-red-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>
                          {comp.status === "active" ? "Completo" : comp.status === "inactive" ? "Desativado" : "Pendente"}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex gap-1">
                          {comp.status !== "inactive" && (
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 text-xs"
                              onClick={() => handleUpdateCompanyStatus(comp.id, "inactive")} data-testid={`deactivate-company-${comp.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Desativar
                            </Button>
                          )}
                          {comp.status === "inactive" && (
                            <Button variant="ghost" size="sm" className="text-green-500 hover:text-green-700 h-7 text-xs"
                              onClick={() => handleUpdateCompanyStatus(comp.id, "active")} data-testid={`activate-company-${comp.id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Ativar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Affiliates List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Afiliados</CardTitle>
              <CardDescription>Clique no afiliado para ver todos os detalhes</CardDescription>
            </div>
            {affiliates.length > 0 && (
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Buscar por nome, login, email ou telefone..."
                  value={affiliateSearch}
                  onChange={(e) => setAffiliateSearch(e.target.value)}
                  className="pl-9 pr-9 text-sm"
                  data-testid="search-affiliate"
                />
                {affiliateSearch && (
                  <button
                    type="button"
                    onClick={() => setAffiliateSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title="Limpar busca"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {affiliates.length === 0 ? (
            <div className="text-center py-12">
              <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum afiliado cadastrado</p>
            </div>
          ) : (() => {
            const q = affiliateSearch.trim().toLowerCase();
            const filtered = q
              ? affiliates.filter(a => {
                  return (
                    (a.name || "").toLowerCase().includes(q) ||
                    (a.login || "").toLowerCase().includes(q) ||
                    (a.email || "").toLowerCase().includes(q) ||
                    (a.phone || "").toLowerCase().includes(q) ||
                    (a.cpf_cnpj || "").toLowerCase().includes(q)
                  );
                })
              : affiliates;
            if (filtered.length === 0) {
              return (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum afiliado encontrado para "<strong>{affiliateSearch}</strong>"</p>
                  <button onClick={() => setAffiliateSearch("")} className="text-[#1CA1BA] text-sm mt-2 hover:underline">Limpar busca</button>
                </div>
              );
            }
            return (
            <>
            {q && (
              <p className="text-xs text-slate-500 mb-3">{filtered.length} de {affiliates.length} afiliado{affiliates.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="affiliates-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Nome</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Login</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Telefone</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Indicacoes</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Ativos</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Recorrencia/mes</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((aff) => (
                    <tr key={aff.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => handleViewDetail(aff)}>
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-900">{aff.name}</p>
                        <p className="text-xs text-slate-500">{aff.email}</p>
                      </td>
                      <td className="py-3 px-2 text-slate-600 font-mono">{aff.login}</td>
                      <td className="py-3 px-2 text-slate-600">{aff.phone}</td>
                      <td className="py-3 px-2 font-medium">{aff.total_referrals || 0}</td>
                      <td className="py-3 px-2 font-medium text-green-600">{aff.active_referrals || 0}</td>
                      <td className="py-3 px-2 font-medium text-purple-600">R$ {(aff.monthly_recurring || 0).toFixed(2)}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${aff.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {aff.active ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetail(aff)} data-testid={`view-affiliate-${aff.login}`} title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(aff)} className="text-blue-600 hover:text-blue-700" title="Editar dados (nome, email, login, etc.)" data-testid={`edit-aff-${aff.login}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenResetPassword(aff)} className="text-amber-600 hover:text-amber-700" title="Redefinir senha" data-testid={`reset-pwd-${aff.login}`}>
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(aff)} className={aff.active ? "text-orange-500 hover:text-orange-700" : "text-green-500 hover:text-green-700"} title={aff.active ? "Desativar (mantém dados)" : "Reativar"}>
                            {aff.active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePermanent(aff)} className="text-red-600 hover:text-red-800 hover:bg-red-50" title="Excluir PERMANENTEMENTE" data-testid={`delete-aff-${aff.login}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create Affiliate Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Afiliado</DialogTitle>
            <DialogDescription>Cadastre um novo afiliado no programa CiaPonto Indica+</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nome Completo *</Label>
                <Input value={newAffiliate.name} onChange={(e) => setNewAffiliate({...newAffiliate, name: e.target.value})} placeholder="Nome do afiliado" data-testid="aff-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newAffiliate.email} onChange={(e) => setNewAffiliate({...newAffiliate, email: e.target.value})} placeholder="email@exemplo.com" data-testid="aff-email" />
              </div>
              <div className="space-y-2">
                <Label>CPF/CNPJ</Label>
                <Input value={newAffiliate.cpf_cnpj} onChange={(e) => setNewAffiliate({...newAffiliate, cpf_cnpj: e.target.value})} placeholder="000.000.000-00" data-testid="aff-cpf" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={newAffiliate.phone} onChange={(e) => setNewAffiliate({...newAffiliate, phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="aff-phone" />
              </div>
              <div className="space-y-2">
                <Label>PIX (para pagamento)</Label>
                <Input value={newAffiliate.pix} onChange={(e) => setNewAffiliate({...newAffiliate, pix: e.target.value})} placeholder="Chave PIX" data-testid="aff-pix" />
              </div>
              <div className="space-y-2">
                <Label>Login *</Label>
                <Input value={newAffiliate.login} onChange={(e) => setNewAffiliate({...newAffiliate, login: e.target.value})} placeholder="Login de acesso" data-testid="aff-login" />
              </div>
              <div className="space-y-2">
                <Label>Senha *</Label>
                <Input type="password" value={newAffiliate.password} onChange={(e) => setNewAffiliate({...newAffiliate, password: e.target.value})} placeholder="Senha" data-testid="aff-password" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Dia de Pagamento das Recorrencias</Label>
                <Input type="number" min="1" max="31" value={newAffiliate.payment_day} onChange={(e) => setNewAffiliate({...newAffiliate, payment_day: parseInt(e.target.value) || 10})} data-testid="aff-payment-day" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#1CA1BA] hover:bg-[#17889E]" disabled={submitting} data-testid="aff-submit-btn">
                {submitting ? "Criando..." : "Criar Afiliado"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Affiliate Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { if (!open) { setShowEditModal(false); setEditingAffiliate(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" /> Editar Afiliado
            </DialogTitle>
            <DialogDescription>
              Edite os dados cadastrais do afiliado. Para alterar a senha, use o botão de chave (🔑).
            </DialogDescription>
          </DialogHeader>
          {editingAffiliate && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Nome Completo *</Label>
                <Input
                  value={editingAffiliate.name}
                  onChange={(e) => setEditingAffiliate({ ...editingAffiliate, name: e.target.value })}
                  data-testid="edit-aff-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Login *</Label>
                  <Input
                    value={editingAffiliate.login}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, login: e.target.value })}
                    data-testid="edit-aff-login"
                  />
                  {editingAffiliate.login !== editingAffiliate._original_login && (
                    <p className="text-xs text-amber-600">⚠️ O afiliado terá que usar o novo login para entrar</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Email</Label>
                  <Input
                    type="email"
                    value={editingAffiliate.email}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, email: e.target.value })}
                    data-testid="edit-aff-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">CPF / CNPJ</Label>
                  <Input
                    value={editingAffiliate.cpf_cnpj}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, cpf_cnpj: e.target.value })}
                    data-testid="edit-aff-cpfcnpj"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Telefone</Label>
                  <Input
                    value={editingAffiliate.phone}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, phone: e.target.value })}
                    data-testid="edit-aff-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Chave PIX</Label>
                  <Input
                    value={editingAffiliate.pix}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, pix: e.target.value })}
                    data-testid="edit-aff-pix"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Dia de Pagamento</Label>
                  <Input
                    type="number" min="1" max="28"
                    value={editingAffiliate.payment_day}
                    onChange={(e) => setEditingAffiliate({ ...editingAffiliate, payment_day: e.target.value })}
                    data-testid="edit-aff-payday"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                <strong>Dica:</strong> Espaços extras no início/fim do login serão removidos automaticamente ao salvar.
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingAffiliate(null); }} className="flex-1" disabled={savingEdit}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit || !editingAffiliate.name?.trim() || !editingAffiliate.login?.trim()}
                  className="flex-1 bg-[#1CA1BA] hover:bg-[#17889E]"
                  data-testid="save-edit-aff"
                >
                  {savingEdit ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={(open) => { if (!open) handleCloseResetPassword(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              {resetPasswordSuccess ? "Senha Redefinida!" : "Redefinir Senha do Afiliado"}
            </DialogTitle>
            <DialogDescription>
              {resetPasswordSuccess
                ? "A senha foi atualizada no sistema. Anote ou envie estas credenciais ao afiliado."
                : <>Defina uma nova senha para <strong>{resetPasswordTarget?.name}</strong> (login: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{resetPasswordTarget?.login}</code>)</>
              }
            </DialogDescription>
          </DialogHeader>

          {resetPasswordSuccess ? (
            <div className="space-y-4 py-2">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-700 font-semibold">
                  <CheckCircle className="w-5 h-5" /> Credenciais ativas agora:
                </div>
                <div className="bg-white rounded p-3 space-y-2 font-mono text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">LOGIN</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(resetPasswordSuccess.login); toast.success("Login copiado"); }}
                      className="text-xs text-[#1CA1BA] hover:underline"
                    >Copiar</button>
                  </div>
                  <div className="font-semibold text-slate-900 break-all">{resetPasswordSuccess.login}</div>
                  <hr className="border-slate-100" />
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">SENHA</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(resetPasswordSuccess.password); toast.success("Senha copiada"); }}
                      className="text-xs text-[#1CA1BA] hover:underline"
                      data-testid="copy-pwd-btn"
                    >Copiar</button>
                  </div>
                  <div className="font-semibold text-slate-900 break-all">{resetPasswordSuccess.password}</div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                <strong>Importante:</strong> Caso o afiliado ainda receba "Credenciais inválidas" ao tentar logar:
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>Peça para limpar o cache do navegador (Ctrl+Shift+R)</li>
                  <li>Ou tentar em uma aba anônima</li>
                  <li>Verifique se está digitando o login <code className="bg-white px-1">{resetPasswordSuccess.login}</code> exatamente igual (sem espaços)</li>
                </ul>
              </div>
              <Button onClick={handleCloseResetPassword} className="w-full bg-[#1CA1BA] hover:bg-[#17889E]">
                Fechar
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  <strong>Por segurança</strong>, as senhas são armazenadas com hash bcrypt e <strong>não podem ser visualizadas</strong>. Você pode <strong>definir uma nova senha</strong> abaixo e informá-la ao afiliado.
                </div>
                <div className="space-y-2">
                  <Label>Nova senha *</Label>
                  <div className="relative">
                    <Input
                      type={showNewPasswordPlain ? "text" : "password"}
                      value={newAffiliatePassword}
                      onChange={(e) => setNewAffiliatePassword(e.target.value)}
                      placeholder="Mínimo 4 caracteres"
                      data-testid="reset-pwd-input"
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPasswordPlain(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      title={showNewPasswordPlain ? "Ocultar" : "Mostrar"}
                    >
                      {showNewPasswordPlain ? <EyeOff className="w-4 h-4" /> : <EyeOpen className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="text-xs text-[#1CA1BA] hover:underline"
                    data-testid="generate-pwd-btn"
                  >
                    Gerar senha aleatória forte
                  </button>
                </div>
                {newAffiliatePassword && showNewPasswordPlain && (
                  <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                    <strong>Pré-visualização:</strong> <code className="font-mono">{newAffiliatePassword}</code>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleCloseResetPassword} className="flex-1" disabled={resettingPassword}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmResetPassword}
                  disabled={resettingPassword || !newAffiliatePassword || newAffiliatePassword.length < 4}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-reset-pwd"
                >
                  {resettingPassword ? "Salvando..." : "Salvar Nova Senha"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Main Super Admin Dashboard
export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Dashboard", href: "/superadmin", icon: Home },
    { name: "Empresas", href: "/superadmin/companies", icon: Building2 },
    { name: "Financeiro", href: "/superadmin/financeiro", icon: DollarSign },
    { name: "Notas Fiscais", href: "/superadmin/nfse", icon: Receipt },
    { name: "Visitantes", href: "/superadmin/visitantes", icon: Eye },
    { name: "Documentos", href: "/superadmin/documentos", icon: FileText },
    { name: "Indica+", href: "/superadmin/afiliados", icon: Handshake },
    { name: "Backup", href: "/superadmin/backup", icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (href) => {
    if (href === "/superadmin") return location.pathname === "/superadmin";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#137082]">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#1CA1BA] transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-white/20">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-ciaponto-color.png" 
              alt="CiaPonto" 
              className="h-8 w-auto brightness-0 invert"
            />
            <div>
              <p className="text-xs text-cyan-400">Super Admin</p>
            </div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive(item.href)
                  ? "bg-white/25 text-white"
                  : "text-white/75 hover:text-white hover:bg-white/15"
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1CA1BA]-dark rounded-full flex items-center justify-center">
              <span className="font-medium text-white">{user?.name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-cyan-400">Super Admin</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-white/75 hover:text-white hover:bg-white/15"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
          
          {/* Powered by CiaPonto */}
          <div className="mt-3 pt-3 border-t border-white/20 text-center">
            <a 
              href="https://www.ciaponto.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-white/60 hover:text-white transition-colors"
            >
              Powered by <span className="font-semibold">CiaPonto</span>
            </a>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="bg-[#1CA1BA] border-b border-[#17889E] sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 h-16">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-400"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-300 hover:text-white">
                  <div className="w-8 h-8 bg-[#1CA1BA]-dark rounded-full flex items-center justify-center">
                    <span className="font-medium text-white">{user?.name?.[0]}</span>
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-[calc(100vh-64px)]">
          <Routes>
            <Route index element={<SuperAdminOverview />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="financeiro" element={<FinanceiroDashboard />} />
            <Route path="nfse" element={<NotasFiscaisPage />} />
            <Route path="visitantes" element={<VisitantesPage />} />
            <Route path="documentos" element={<DocumentosPage />} />
            <Route path="afiliados" element={<AfiliadosPage />} />
            <Route path="backup" element={<BackupPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
